import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { useLocation } from "react-router-dom"
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
  Grid,
  Button,
  Menu,
  MenuItem,
  Collapse,
  CircularProgress,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import OpenInFullIcon from "@mui/icons-material/OpenInFull"
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import {
  LivisCopilotIcon,
  ModelTrainingIcon,
  InspectorIcon,
  AddNoPartIcon,
  DataAnalyticsIcon,
  CopilotAlertIcon,
  AlertBoldIcon,
  CopilotHistoryIcon,
  CopilotEditIcon,
  CopilotSidebarIcon,
  CopilotBoxIcon,
  CopilotShapeIcon,
} from "../../Icons/LivisIcons"
import { toast } from "react-toastify"
import { Home, MessageCircle, HelpCircle, Megaphone } from "lucide-react"
import EditIcon from "@mui/icons-material/Edit"
import ViewSidebarOutlinedIcon from "@mui/icons-material/ViewSidebarOutlined"
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline"
import NotificationsOutlinedIcon from "@mui/icons-material/NotificationsOutlined"
import HistoryIcon from "@mui/icons-material/History"
import WarningAmberIcon from "@mui/icons-material/WarningAmber"
import CheckIcon from "@mui/icons-material/Check"
import AddIcon from "@mui/icons-material/Add"

import SearchIcon from "@mui/icons-material/Search"
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined"
import ThumbDownAltOutlinedIcon from "@mui/icons-material/ThumbDownAltOutlined"
import RepeatIcon from "@mui/icons-material/Repeat"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import utc from "dayjs/plugin/utc"
import {
  getAgentSessions as getSessions,
  getAgentSession as getSession,
  createAgentSession as createSession,
  clearAgentChat,
  deleteAgentSession as deleteSession,
  renameAgentSession as renameSession,
  askAgent as askRag,
  sendAgentEvent,
  postAgentFeedback,
  resolveUserEmail,
} from "../../../services/aiAgent"
import { useAgentWebSocket } from "../../../hooks/useAgentWebSocket"
import {
  getCopilotUiContext,
  getCopilotWorkBinding,
  getHomeHelpPrompts,
  getRouteHomeActionChips,
  getRouteStarterChips,
  routeHelpOnPageTitle,
  routeContextHeadline,
  routeHomeSection,
  type CopilotStarterChip,
} from "../context/copilotPageContext"
import { getAllAlertsAPI } from "../../../services/notificationService"
import type { Envelope } from "../../../services/aiAgentWS"
import { AgentMarkdown } from "../markdown/AgentMarkdown"
import helpSidebar from "../../../modules/help/data/sidebar.json"
import { AgentMarker, applyTerminology } from "../markers/AgentMarkers"
import { AIHelpContentViewer } from "../help/AIHelpContentViewer"
import {
  ChatInputArea,
  MAX_QUERY_LENGTH,
  MentionTarget,
  ActiveContext,
  ContextCrumb,
} from "./ChatInputBar"
import { formatSessionListTime, getSessionTimestamp } from "./sessionTime"
import { livisAlert } from "../../../helpers/alert"
import { formatString } from "../../../helpers/utils"
import { getLivisUserInfo } from "../../../helpers/storage"
import { useTranslation } from "react-i18next"
import LivisModal from "../../Modals/LivisModal"

dayjs.extend(relativeTime)
dayjs.extend(utc)

export type ChatMode = "floating" | "fullscreen"

export interface SessionSummary {
  session_id: string
  summary: string
  title?: string
  last_active?: number | string
  updated_at?: number | string
  created_at?: number | string
}

/** Above `.livis-ai-assistant-chat-window` (999999) and modals — tooltips/menus use this. */
const AI_CHAT_OVERLAY_Z_INDEX = 9999999

export interface AgentMarkerData {
  id: string
  payload: any
  disabled?: boolean
}

export interface Message {
  id: string
  type: "user" | "ai"
  text: string
  isLoading?: boolean
  createdAt?: string | number
  debugTrace?: {
    runtime_route?: string
    tool_name?: string
    collections_checked?: string[]
  }
  markers?: AgentMarkerData[]
  statuses?: {
    id: string
    state: string
    label: string
    detail?: string
    progress?: number
    archived?: boolean
  }[]
  feedbackRating?: "up" | "down"
  feedbackRecorded?: boolean
  steps?: { text: string; ts: number }[]
  startedAt?: number
  finishedAt?: number
}

const newAiPlaceholder = (id: string): Message => ({
  id,
  type: "ai",
  text: "",
  isLoading: true,
  createdAt: new Date().toISOString(),
  // startedAt is set on first onStatus (first WS frame from backend), not at send time.
  // This measures actual backend processing time, not round-trip + auth overhead.
  steps: [],
})

const isEmptyLoadingPlaceholder = (m: Message): boolean =>
  m.type === "ai" &&
  !!m.isLoading &&
  !(m.text || "").trim() &&
  !(m.markers || []).length &&
  !(m.statuses || []).length

const withSingleLoadingPlaceholder = (
  prev: Message[],
  id: string
): Message[] => {
  const cleaned = (prev || []).filter(
    (m) => !isEmptyLoadingPlaceholder(m) && m.id !== id
  )
  return [...cleaned, newAiPlaceholder(id)]
}

/** Workflow markers — regenerating these would re-run hidden agent state, not LLM sampling. */
const REGENERATE_BLOCK_MARKER_TYPES = new Set([
  "choose",
  "input",
  "form",
  "multi_step_form",
  "upload_request",
  "confirm",
  "plan",
  "option_select",
  "repeating_input",
  "navigate",
  "quick_replies",
  "action_menu",
  "agent_plan_review",
  "project_status_card",
])

const isDatasetAnalyzeReport = (msg: Message | undefined): boolean => {
  const t = (msg?.text || "").toLowerCase()
  return (
    t.includes("data analysis") ||
    t.includes("photo quality score") ||
    t.includes("defect class coverage")
  )
}

const inferRegenerateGoal = (msg: Message | undefined): string | undefined => {
  const t = (msg?.text || "").toLowerCase()
  if (isDatasetAnalyzeReport(msg)) return "analyze_data"
  if (
    t.includes("training progress") ||
    (t.includes("epoch") && t.includes("batch"))
  ) {
    return "check_training"
  }
  if (t.includes("retrain")) return "retrain_model"
  return undefined
}

/** Regenerate only for free-text LLM answers (Q&A), not forms/actions/plan steps. */
const isRegenerableAiMessage = (msg: Message | undefined): boolean => {
  if (!msg || msg.type !== "ai" || msg.isLoading) return false
  if (!(msg.text || "").trim()) return false
  if ((msg.statuses || []).length > 0) return false
  if (isDatasetAnalyzeReport(msg)) return true
  for (const mk of msg.markers || []) {
    const mt = mk?.payload?.marker_type
    if (mt && REGENERATE_BLOCK_MARKER_TYPES.has(mt)) return false
  }
  return true
}

/** MUI tooltip only when truncated text shows ellipsis (messages list titles). */

function workBindingTypeLabel(boundType: string): string {
  switch (boundType) {
    case "inspector":
      return "AI Inspector"
    case "part":
      return "Part"
    case "training":
      return "Training"
    default:
      return boundType
  }
}

const EllipsisTooltip: React.FC<{
  text: string
  className?: string
  placement?: "top" | "bottom" | "left" | "right"
}> = ({ text, className, placement = "top" }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [overflow, setOverflow] = useState(false)

  const updateOverflow = useCallback(() => {
    const el = ref.current
    if (!el) return
    setOverflow(el.scrollWidth > el.clientWidth + 1)
  }, [])

  useEffect(() => {
    updateOverflow()
    const el = ref.current
    if (!el || typeof ResizeObserver === "undefined") return undefined
    const ro = new ResizeObserver(updateOverflow)
    ro.observe(el)
    return () => ro.disconnect()
  }, [text, updateOverflow])

  return (
    <Tooltip
      title={text}
      arrow
      placement={placement}
      enterDelay={300}
      disableHoverListener={!overflow}
      disableFocusListener={!overflow}
      PopperProps={{
        sx: { zIndex: AI_CHAT_OVERLAY_Z_INDEX },
        style: { zIndex: AI_CHAT_OVERLAY_Z_INDEX },
      }}
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: 320,
            fontSize: "0.8125rem",
            lineHeight: 1.35,
          },
        },
      }}
    >
      <Typography
        ref={ref}
        component="div"
        noWrap
        className={className}
        onMouseEnter={updateOverflow}
        sx={{ minWidth: 0 }}
      >
        {text}
      </Typography>
    </Tooltip>
  )
}

export interface AIChatWindowProps {
  mode: ChatMode
  setMode?: (mode: ChatMode) => void
  onClose: () => void
  messages: Message[]
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>
  sessionId: string
  setSessionId: React.Dispatch<React.SetStateAction<string>>
  isLoading: boolean
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  currentStatus: string
  setCurrentStatus: React.Dispatch<React.SetStateAction<string>>
  abortControllerRef: React.MutableRefObject<AbortController | null>
}

// ── Thinking accordion ────────────────────────────────────────────────────────
const isThinkingStep = (text: string) => text.startsWith("Thinking:")

const ThoughtAccordion: React.FC<{
  isThinking: boolean
  startedAt?: number
  finishedAt?: number
  steps?: { text: string; ts: number }[]
  currentStatus?: string
  hasResponse?: boolean
}> = ({
  isThinking,
  startedAt,
  finishedAt,
  steps,
  currentStatus,
  hasResponse,
}) => {
  const [open, setOpen] = useState(isThinking)
  const [elapsed, setElapsed] = useState<string>("0")
  const listRef = useRef<HTMLDivElement>(null)

  const lastStepTs =
    steps && steps.length > 0 ? steps[steps.length - 1].ts : undefined
  const isActuallyThinking = isThinking && !hasResponse

  useEffect(() => {
    if (!isActuallyThinking) {
      setOpen(false)
    } else {
      setOpen(true)
    }
  }, [isActuallyThinking])

  useEffect(() => {
    let interval: NodeJS.Timeout

    const updateTime = () => {
      if (!startedAt) return

      let endTs = Date.now()

      if (hasResponse || !isThinking) {
        if (lastStepTs && lastStepTs > startedAt) {
          endTs = lastStepTs
        } else if (finishedAt) {
          endTs = finishedAt
        }
      }

      setElapsed(Math.max(0, Math.floor((endTs - startedAt) / 1000)).toString())
    }

    updateTime()

    if (isThinking && !hasResponse && startedAt) {
      interval = setInterval(updateTime, 1000)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isThinking, startedAt, finishedAt, hasResponse, lastStepTs])

  const visibleSteps =
    steps && steps.length > 0
      ? steps
      : currentStatus
      ? [{ text: currentStatus, ts: Date.now() }]
      : []
  const hasSteps = visibleSteps.length > 0

  useEffect(() => {
    if (open && listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight
    }
  }, [visibleSteps, open])

  if (!hasSteps && !isThinking) return null

  return (
    <Box sx={{ mb: 1, maxWidth: "100%", alignSelf: "flex-start" }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          cursor: "pointer",
          userSelect: "none",
          py: 0.5,
          color: "var(--chat-text-2, var(--livis-gray-font-color))",
          transition: "all 0.2s",
          "&:hover": {
            color: "var(--chat-text-1, var(--livis-black-font-color))",
          },
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: "14px",
            height: "14px",
            "& svg": {
              width: "14px !important",
              height: "14px !important",
              color: isActuallyThinking
                ? "var(--livis-color-primary-blue, #3b82f6) !important"
                : "inherit",
              animation: isActuallyThinking
                ? "livis-ai-assistant-pulse-orb 1.5s infinite"
                : "none",
            },
          }}
        >
          <LivisCopilotIcon />
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            fontSize: "0.85rem",
          }}
        >
          {isActuallyThinking
            ? `Working on it (${elapsed}s)`
            : `Worked for ${elapsed}s`}
        </Typography>
        <KeyboardArrowDownIcon
          sx={{
            fontSize: "1.1rem",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s",
          }}
        />
      </Box>
      <Collapse in={open}>
        <Box
          ref={listRef}
          sx={{
            mt: 0.25,
            ml: 1,
            pl: 1.5,
            borderLeft:
              "2px solid var(--chat-border, var(--livis-border-color))",
            display: "flex",
            flexDirection: "column",
            gap: 0.75,
            maxHeight: "100px",
            overflowY: "auto",
          }}
        >
          {visibleSteps.map((s, i) => {
            const isLast = i === visibleSteps.length - 1
            const showAsInProgress = isActuallyThinking && isLast
            const isThinkingText = isThinkingStep(s.text)

            return (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  opacity: showAsInProgress ? 1 : 0.75,
                  animation:
                    "livis-ai-assistant-fade-in-up 0.3s ease-out forwards",
                }}
              >
                {showAsInProgress ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "14px",
                      height: "1.2rem",
                      flexShrink: 0,
                      mt: 0,
                    }}
                  >
                    <span
                      style={{
                        display: "inline-block",
                        width: "6px",
                        height: "6px",
                        background: "var(--livis-color-primary-blue, #3b82f6)",
                        borderRadius: "50%",
                        animation: "livis-ai-assistant-pulse-orb 1s infinite",
                      }}
                    />
                  </Box>
                ) : isThinkingText ? (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.8rem",
                      color: "var(--livis-color-primary-blue, #3b82f6)",
                      mt: "1px",
                      flexShrink: 0,
                      width: "14px",
                      textAlign: "center",
                    }}
                  >
                    💭
                  </Typography>
                ) : (
                  <CheckIcon
                    sx={{
                      fontSize: "0.85rem",
                      color: "var(--livis-color-primary-blue, #3b82f6)",
                      mt: "2px",
                      flexShrink: 0,
                      width: "14px",
                    }}
                  />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: "var(--chat-text-2, var(--livis-gray-font-color))",
                    fontStyle: isThinkingText ? "italic" : "normal",
                    lineHeight: 1.5,
                    fontSize: "0.8rem",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {s.text.replace(/^Thinking:\s*/, "")}
                </Typography>
              </Box>
            )
          })}
        </Box>
      </Collapse>
    </Box>
  )
}

const playChatSound = (type: "send" | "receive") => {
  try {
    const AudioContext =
      window.AudioContext || (window as any).webkitAudioContext
    if (!AudioContext) return
    const ctx = new AudioContext()
    const osc = ctx.createOscillator()
    const gain = ctx.createGain()

    osc.connect(gain)
    gain.connect(ctx.destination)

    if (type === "send") {
      osc.type = "sine"
      osc.frequency.setValueAtTime(300, ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(500, ctx.currentTime + 0.1)
      gain.gain.setValueAtTime(0, ctx.currentTime)
      gain.gain.linearRampToValueAtTime(0.1, ctx.currentTime + 0.05)
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.1)
    } else {
      osc.type = "sine"
      osc.frequency.setValueAtTime(600, ctx.currentTime)
      osc.frequency.exponentialRampToValueAtTime(300, ctx.currentTime + 0.15)
      gain.gain.setValueAtTime(0, ctx.currentTime)
      gain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 0.02)
      gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.15)
      osc.start(ctx.currentTime)
      osc.stop(ctx.currentTime + 0.15)
    }
  } catch (e) {
    // Ignore autoplay policy errors or lack of support
  }
}

// Stable no-op for action_status markers that need no interaction.
const COPILOT_ACTION_CHIP_IDS = new Set([
  "analyze_data",
  "start_training",
  "check_training",
  "guide_upload",
  "add_part",
  "add_usecase",
  "create_station",
  "create_ws",
  "create_uc",
  "add_another_part",
  "augment_data",
  "show_all_inspectors",
  "retrain_model",
  "switch_part",
  "switch_inspector",
  "switch_station",
  "what_can_you_do",
  "upload_data",
])

const NOOP_ACTION = () => undefined

function ProseBlock({
  text,
  isStreaming,
}: {
  text: string
  isStreaming?: boolean
}) {
  return (
    <Box className="livis-ai-prose-wrap">
      <AgentMarkdown text={text} isStreaming={isStreaming} />
    </Box>
  )
}

// ── MessageBubble ─────────────────────────────────────────────────────────────
// Memoized per message. Creates a stable onAction binding so AgentMarker's
// memo check passes on every keystroke (the inline arrow in the parent map
// was the primary cause of input lag).
interface MessageBubbleProps {
  msg: Message
  sessionId: string
  inputLocked?: boolean
  onMarkerAction: (
    msgId: string,
    markerId: string,
    action: string,
    value: any
  ) => void
  onUploadComplete: (fileRef: string, kind: string, context: any) => void
  loadingStatus?: string
  onRegenerate?: (msgId: string) => void
  onFeedback?: (msgId: string, rating: "up" | "down") => void
  showRegenerate?: boolean
  canEditUser?: boolean
  isEditingUser?: boolean
  editDraft?: string
  onStartEditUser?: () => void
  onEditDraftChange?: (value: string) => void
  onCancelEditUser?: () => void
  onSaveEditUser?: () => void
}

const MessageBubble = React.memo(
  function MessageBubble({
    msg,
    sessionId,
    inputLocked,
    onMarkerAction,
    onUploadComplete,
    loadingStatus,
    onRegenerate,
    onFeedback,
    showRegenerate,
    canEditUser,
    isEditingUser,
    editDraft,
    onStartEditUser,
    onEditDraftChange,
    onCancelEditUser,
    onSaveEditUser,
  }: MessageBubbleProps) {
    const { t } = useTranslation()
    const handleAction = useCallback(
      (markerId: string, action: string, value: any) =>
        onMarkerAction(msg.id, markerId, action, value),
      [msg.id, onMarkerAction]
    )

    if (
      msg?.type === "ai" &&
      !msg.isLoading &&
      !msg.text &&
      !msg.statuses?.length &&
      !msg.markers?.length
    ) {
      return null
    }

    // In-flight turn: dots only (no LIVIS Copilot footer, statuses, or markers yet).
    if (msg?.type === "ai" && msg.isLoading) {
      const streamingText = (msg.text || "").trim()
      if (!streamingText) {
        return (
          <Box className="livis-ai-thinking-wrap">
            <ThoughtAccordion
              isThinking={true}
              startedAt={msg.startedAt}
              steps={msg.steps}
              currentStatus={loadingStatus}
              hasResponse={false}
            />
          </Box>
        )
      }
      return (
        <Box className="livis-ai-assistant-message ai">
          <ThoughtAccordion
            isThinking={true}
            startedAt={msg.startedAt}
            steps={msg.steps}
            currentStatus={loadingStatus}
            hasResponse={true}
          />
          <AgentMarkdown text={msg.text} isStreaming={true} />
        </Box>
      )
    }

    return (
      <>
        {msg?.type === "user" ? (
          <Box
            className={`livis-ai-user-turn${
              isEditingUser ? " is-editing-turn" : ""
            }`}
          >
            <Box
              className={`livis-ai-assistant-message user${
                isEditingUser ? " is-editing" : ""
              }`}
            >
              {isEditingUser ? (
                <Box className="livis-ai-assistant-message-meta user-meta">
                  <Typography component="span" className="meta-name">
                    {t("Edit message")}
                  </Typography>
                </Box>
              ) : (
                <Box className="livis-ai-assistant-message-meta user-meta">
                  <Typography component="span" className="meta-name">
                    You
                  </Typography>
                  {msg?.createdAt && (
                    <Typography component="span" className="meta-dot">
                      •
                    </Typography>
                  )}
                  {msg?.createdAt && (
                    <Typography component="span" className="meta-time">
                      {dayjs.utc(msg.createdAt).local().format("h:mm A")}
                    </Typography>
                  )}
                </Box>
              )}
              {isEditingUser ? (
                <Box className="livis-ai-user-edit-box">
                  <textarea
                    className="livis-ai-user-edit-field"
                    rows={3}
                    autoFocus
                    value={editDraft ?? ""}
                    onChange={(e) => onEditDraftChange?.(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        e.preventDefault()
                        onCancelEditUser?.()
                        return
                      }
                      if (
                        e.key === "Enter" &&
                        (e.metaKey || e.ctrlKey) &&
                        (editDraft || "").trim() &&
                        !inputLocked
                      ) {
                        e.preventDefault()
                        onSaveEditUser?.()
                      }
                    }}
                    disabled={inputLocked}
                  />
                  <Box className="livis-ai-user-edit-actions">
                    <button
                      type="button"
                      className="livis-ai-user-edit-cancel"
                      onClick={onCancelEditUser}
                      disabled={inputLocked}
                    >
                      {t("Cancel")}
                    </button>
                    <button
                      type="button"
                      className="livis-ai-user-edit-save"
                      onClick={onSaveEditUser}
                      disabled={inputLocked || !(editDraft || "").trim()}
                    >
                      {t("Send")}
                    </button>
                  </Box>
                </Box>
              ) : msg.text ? (
                <Typography
                  variant="body2"
                  component="div"
                  className="livis-ai-user-message-text"
                >
                  {renderInlineBold(msg.text)}
                </Typography>
              ) : null}
            </Box>
            {!isEditingUser && canEditUser ? (
              <Tooltip title={t("Edit message") as string} arrow>
                <span>
                  <IconButton
                    size="small"
                    className="livis-ai-user-edit-icon"
                    aria-label={t("Edit message") as string}
                    disabled={inputLocked}
                    onClick={onStartEditUser}
                  >
                    <EditOutlinedIcon
                      sx={{ fontSize: "0.95rem", color: "inherit" }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        ) : null}

        {msg?.type === "ai" ? (
          <Box className={`livis-ai-assistant-message ${msg?.type}`}>
            <ThoughtAccordion
              isThinking={false}
              startedAt={msg.startedAt}
              finishedAt={msg.finishedAt}
              steps={msg.steps}
              hasResponse={!!msg.text}
            />
            {msg.text ? (
              <ProseBlock text={msg.text} isStreaming={msg.isLoading} />
            ) : null}
            {(msg.statuses || []).map((s) => (
              <Box
                key={s.id}
                sx={{
                  opacity: s.archived ? 0.35 : 1,
                  transition: "opacity 0.3s",
                }}
              >
                <AgentMarker
                  id={s.id}
                  payload={{ marker_type: "action_status", ...s }}
                  sessionId={sessionId}
                  onAction={NOOP_ACTION}
                />
              </Box>
            ))}
            {(msg.markers || [])
              .filter(
                (mk) =>
                  !["quick_replies", "action_menu", "sources"].includes(
                    mk?.payload?.marker_type
                  )
              )
              .map((mk) => (
                <div
                  key={mk.id}
                  className="livis-ai-marker-wrapper"
                  data-marker-type={mk?.payload?.marker_type}
                >
                  <AgentMarker
                    id={mk.id}
                    payload={mk.payload}
                    sessionId={sessionId}
                    disabled={mk.disabled || inputLocked}
                    onAction={handleAction}
                    onUploadComplete={onUploadComplete}
                  />
                </div>
              ))}
            {(msg.markers || [])
              .filter((mk) =>
                ["quick_replies", "action_menu", "sources"].includes(
                  mk?.payload?.marker_type
                )
              )
              .map((mk) => (
                <div
                  key={mk.id}
                  className="livis-ai-marker-wrapper"
                  data-marker-type={mk?.payload?.marker_type}
                >
                  <AgentMarker
                    id={mk.id}
                    payload={mk.payload}
                    sessionId={sessionId}
                    disabled={mk.disabled || inputLocked}
                    onAction={handleAction}
                    onUploadComplete={onUploadComplete}
                  />
                </div>
              ))}
            <Box
              className={`livis-ai-message-footer${
                msg.feedbackRecorded ? " has-feedback" : ""
              }`}
            >
              <Box className="livis-ai-meta-actions-row">
                {!msg.isLoading && (msg.text || (msg.markers || []).length) ? (
                  <Box className="livis-ai-message-actions">
                    {msg.feedbackRecorded && msg.feedbackRating ? (
                      <Tooltip
                        title={
                          msg.feedbackRating === "up"
                            ? (t("Marked as helpful") as string)
                            : (t("Marked as not helpful") as string)
                        }
                        arrow
                      >
                        <span>
                          <IconButton
                            size="small"
                            aria-label={
                              msg.feedbackRating === "up"
                                ? (t("Marked as helpful") as string)
                                : (t("Marked as not helpful") as string)
                            }
                            className="is-selected livis-ai-feedback-locked"
                            disabled
                          >
                            {msg.feedbackRating === "up" ? (
                              <ThumbUpAltOutlinedIcon fontSize="small" />
                            ) : (
                              <ThumbDownAltOutlinedIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : !msg.feedbackRecorded ? (
                      <>
                        {showRegenerate ? (
                          <Tooltip
                            title={t("Regenerate response") as string}
                            arrow
                          >
                            <span>
                              <IconButton
                                size="small"
                                aria-label={t("Regenerate response") as string}
                                className="livis-ai-regenerate-btn"
                                disabled={inputLocked}
                                onClick={() => onRegenerate?.(msg.id)}
                              >
                                <RepeatIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                        <Tooltip title={t("Mark as helpful") as string} arrow>
                          <span>
                            <IconButton
                              size="small"
                              aria-label={t("Mark as helpful") as string}
                              className={
                                msg.feedbackRating === "up" ? "is-selected" : ""
                              }
                              disabled={inputLocked}
                              onClick={() => onFeedback?.(msg.id, "up")}
                            >
                              <ThumbUpAltOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={t("Mark as not helpful") as string}
                          arrow
                        >
                          <span>
                            <IconButton
                              size="small"
                              aria-label={t("Mark as not helpful") as string}
                              className={
                                msg.feedbackRating === "down"
                                  ? "is-selected"
                                  : ""
                              }
                              disabled={inputLocked}
                              onClick={() => onFeedback?.(msg.id, "down")}
                            >
                              <ThumbDownAltOutlinedIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    ) : null}
                  </Box>
                ) : null}
              </Box>
              {msg.feedbackRecorded ? (
                <Typography
                  component="p"
                  className="livis-ai-feedback-recorded"
                >
                  {t("Your feedback has been recorded")}
                </Typography>
              ) : null}
            </Box>
          </Box>
        ) : null}
      </>
    )
  },
  (prev, next) =>
    prev.msg === next.msg &&
    prev.sessionId === next.sessionId &&
    prev.inputLocked === next.inputLocked &&
    prev.onMarkerAction === next.onMarkerAction &&
    prev.onUploadComplete === next.onUploadComplete &&
    prev.loadingStatus === next.loadingStatus &&
    prev.onRegenerate === next.onRegenerate &&
    prev.onFeedback === next.onFeedback &&
    prev.showRegenerate === next.showRegenerate &&
    prev.canEditUser === next.canEditUser &&
    prev.isEditingUser === next.isEditingUser &&
    prev.editDraft === next.editDraft
)

/** Render inline *bold* and **bold** markers in plain text (user bubbles). */
function renderInlineBold(text: string): React.ReactNode[] {
  return text.split(/(\*\*.*?\*\*|\*[^*]+?\*)/g).map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**"))
      return <strong key={i}>{part.slice(2, -2)}</strong>
    if (part.startsWith("*") && part.endsWith("*"))
      return <strong key={i}>{part.slice(1, -1)}</strong>
    return <React.Fragment key={i}>{part}</React.Fragment>
  })
}

// Action-event messages saved by send_event (e.g. "[action:proceed]") are
// internal records — not real user text. Filter them from history display.
const isActionMessage = (m: any): boolean => {
  const content = (m?.content || "").trim()
  // [action:...] messages are user-role only
  if (m?.role === "user" && /^\[action:/.test(content)) return true
  // Feedback/rating payloads can be stored under any role — filter regardless
  if (content.startsWith("{") && content.endsWith("}")) {
    try {
      const parsed = JSON.parse(content)
      if (
        parsed &&
        typeof parsed === "object" &&
        ("session_id" in parsed ||
          "rating" in parsed ||
          "message_id" in parsed ||
          "user_email" in parsed)
      ) {
        return true
      }
    } catch {
      // Not valid JSON — let it render as a normal message
    }
  }
  return false
}

/** Qdrant may store envelopes as JSON string or nested lists — normalize for history reload. */
const normalizeStoredEnvelopes = (raw: unknown): Envelope[] => {
  if (raw == null) return []
  let value: unknown = raw
  if (typeof value === "string") {
    try {
      value = JSON.parse(value)
    } catch {
      return []
    }
  }
  if (!Array.isArray(value)) return []
  const out: Envelope[] = []
  for (const item of value) {
    if (Array.isArray(item)) {
      out.push(...normalizeStoredEnvelopes(item))
    } else if (item && typeof item === "object" && "type" in (item as object)) {
      out.push(item as Envelope)
    }
  }
  return out
}

/** Text embedded in ui_render markers for history search — confirm/plan cards render via markers only. */
const uiRenderProseFromPayload = (p: Record<string, any>): string => {
  const mt = p?.marker_type
  // confirm + agent_plan_review: summary lives in ConfirmMarker only (avoid duplicate Overview block)
  if (mt === "plan" && p.title)
    return [p.title, p.goal].filter(Boolean).join("\n\n")
  // notification: rendered only via NotificationMarker (avoid duplicate prose + alert)
  if (mt === "option_select" && p.prompt) return p.prompt
  return ""
}

const formatHistoryMessages = (messageData: any[]): Message[] => {
  const formatted: Message[] = []
  messageData.forEach((m: any, idx: number) => {
    if (isActionMessage(m)) return
    const msgId = m?.id || `h-${idx}-${Date.now()}`
    if (m?.role === "assistant" || m?.role === "ai") {
      const msg = buildAiMessageFromEnvelopes(
        normalizeStoredEnvelopes(m.envelopes),
        msgId,
        m.created_at
      )
      if (!msg.text && !msg.markers?.length && !msg.statuses?.length) return
      formatted.push(msg)
      return
    }
    const text = (m?.content || "").trim()
    if (text) {
      formatted.push({ id: msgId, type: "user", text, createdAt: m.created_at })
    }
  })
  return formatted
}

// ── Envelope processing helper ────────────────────────────────────────────────
const buildAiMessageFromEnvelopes = (
  envelopes: Envelope[],
  msgId: string,
  createdAt: string
): Message => {
  let text = ""
  const statusMap: Record<string, any> = {}
  const markersList: AgentMarkerData[] = []
  let debugTrace: Message["debugTrace"] | undefined

  for (const env of envelopes || []) {
    if (env.type === "agent_prose") {
      text += (text ? "\n\n" : "") + (env.payload?.text || "")
      if ((env as any).meta?.runtime_route || (env as any).meta?.tool_name) {
        debugTrace = {
          runtime_route: (env as any).meta?.runtime_route,
          tool_name: (env as any).meta?.tool_name,
          collections_checked: (env as any).meta?.collections_checked || [],
        }
      }
    } else if (env.type === "ui_render") {
      const mid = env.id || `m-${Date.now()}-${Math.random()}`
      const p = env.payload || {}
      if (p.marker_type === "action_status") {
        statusMap[mid] = {
          id: mid,
          state: p.state || "running",
          label: p.label,
          detail: p.detail,
          progress: p.progress,
        }
      } else {
        const embedded = uiRenderProseFromPayload(p)
        if (embedded) text += (text ? "\n\n" : "") + embedded
        markersList.push({ id: mid, payload: p })
      }
    } else if (env.type === "ui_update") {
      const mid = env.id
      const patch = env.payload || {}
      if (mid && statusMap[mid]) {
        statusMap[mid] = { ...statusMap[mid], ...patch }
      }
      if (mid) {
        const idx = markersList.findIndex((mk) => mk.id === mid)
        if (idx >= 0) {
          markersList[idx] = {
            ...markersList[idx],
            payload: { ...(markersList[idx].payload || {}), ...patch },
          }
        }
      }
    } else if (env.type === "ui_remove") {
      if (env.id) delete statusMap[env.id]
    } else if (env.type === "error") {
      text +=
        (text ? "\n\n" : "") + (env.payload?.text || "Something went wrong.")
    }
  }

  return {
    id: msgId,
    type: "ai",
    text,
    isLoading: false,
    createdAt,
    statuses: Object.values(statusMap),
    markers: markersList,
    debugTrace,
  }
}

/** Keep submitted form/selector values visible after disable (read-only). */
const persistMarkerSubmission = (
  marker: AgentMarkerData,
  activeMarkerId: string,
  action: string,
  value: unknown
): AgentMarkerData => {
  // Refresh-status is a repeatable poll action — never permanently disable it.
  if (marker.id === "check_training_status") {
    return marker
  }
  if (marker.id !== activeMarkerId) {
    return { ...marker, disabled: true }
  }
  const payload = { ...(marker.payload || {}) }
  const mt = payload.marker_type as string | undefined

  if (action === "submit") {
    if (
      value !== null &&
      value !== undefined &&
      typeof value === "object" &&
      !Array.isArray(value)
    ) {
      const vals = value as Record<string, unknown>
      payload.submitted_values = vals
      payload.initial_values = { ...(payload.initial_values || {}), ...vals }
    } else if (typeof value === "string" || typeof value === "number") {
      payload.submitted_value = value
    }
  } else if (action === "select") {
    payload.submitted_value = value
    if (Array.isArray(value)) {
      payload.submitted_values = value
    } else {
      payload.selected_value = value
    }
  } else if (mt === "option_select") {
    payload.selected_option_id = action
    const opts = (payload.options as Array<Record<string, unknown>>) || []
    const match = opts.find(
      (o) => String(o.id || o.value || o.label || "") === action
    )
    if (match?.label) payload.selected_option_label = match.label
  }

  return { ...marker, disabled: true, payload }
}

const disableMarkersWithSubmission = (
  markers: AgentMarkerData[] | undefined,
  activeMarkerId: string,
  action: string,
  value: unknown
): AgentMarkerData[] =>
  (markers || []).map((m) =>
    persistMarkerSubmission(m, activeMarkerId, action, value)
  )

/** Non-UI envelopes: tell list pages to re-fetch after agent mutations. */
const applyAgentSideEffects = (envelopes: Envelope[]) => {
  const refreshEnvs = envelopes.filter((e) => e.type === "refresh")
  if (!refreshEnvs.length) return
  const allResources = refreshEnvs.flatMap((e) => e.payload?.resources || [])
  if (allResources.length > 0) {
    window.dispatchEvent(
      new CustomEvent("livis_agent_refresh", {
        detail: { resources: allResources },
      })
    )
  }
}

// ── Main component ────────────────────────────────────────────────────────────
const AIChatWindow: React.FC<AIChatWindowProps> = ({
  mode,
  setMode,
  onClose,
  messages,
  setMessages,
  sessionId,
  setSessionId,
  isLoading,
  setIsLoading,
  currentStatus,
  setCurrentStatus,
  abortControllerRef,
}) => {
  // inputValue moved to ChatInputArea — typing no longer re-renders this tree.
  const location = useLocation()

  const routePageContext = useMemo(() => {
    const ctx = getCopilotUiContext()
    const screen = ctx.screen || ""
    const section = screen ? routeHomeSection(screen, ctx.selection) : null
    const helpNav = (helpSidebar as { sidebar?: { navigation?: unknown[] } })
      ?.sidebar?.navigation
    return {
      screen,
      chips: screen ? getRouteHomeActionChips(screen) : [],
      starterChips: screen ? getRouteStarterChips(screen) : [],
      helpChips: screen
        ? getHomeHelpPrompts(screen, (helpNav || []) as never[])
        : [],
      helpOnTitle: screen ? routeHelpOnPageTitle(screen) : "",
      headline: section?.title || "",
    }
  }, [location.pathname, location.search])

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement | null>(null)
  const [inputMode, setInputMode] = useState<"agent" | "ask">("agent")
  const [editingUserMsgId, setEditingUserMsgId] = useState<string | null>(null)
  const [editUserDraft, setEditUserDraft] = useState("")
  // Debounced connection banner: brief WS reconnects (normal) shouldn't flash a banner —
  // only surface it if the disconnect persists ~2s. Hidden instantly on reconnect.
  const [showConnBanner, setShowConnBanner] = useState(false)
  const [isWideMode, setIsWideMode] = useState(false)
  // Ref guard: only call setIsWideMode when the value actually flips.
  // Prevents scroll-triggered re-renders when wide-mode state is already correct.
  const isWideModeRef = useRef(false)

  // Tracks the AI message ID currently being streamed (null when idle)
  const streamingMsgIdRef = useRef<string | null>(null)
  /** session_start greeting stream — separate from user sends so they don't clobber each other */
  const systemStreamMsgIdRef = useRef<string | null>(null)

  // Track the actual mounted container node so we can reliably bind/unbind observers.
  // A ref callback fires every time React mounts/unmounts the conditionally rendered container.
  const [containerNode, setContainerNode] = useState<HTMLDivElement | null>(
    null
  )
  const messagesContainerCallbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      messagesContainerRef.current = node
      setContainerNode(node)
    },
    []
  )

  /** Session that owns the in-flight WS stream; ignore tokens/done after switching chats */
  const streamSessionRef = useRef<string | null>(null)
  const sessionIdRef = useRef(sessionId)
  const [streamPending, setStreamPending] = useState(false)
  const shouldSendSessionStartRef = useRef(false)
  const autoRestoredSessionsRef = useRef<Set<string>>(new Set())
  const sessionLoadGenRef = useRef(0)
  const [workBinding, setWorkBinding] = useState<{
    bound_type: string
    bound_id: string
    bound_label: string
    source?: "bind" | "entity"
  } | null>(null)

  // Context banner — shows what the AI agent confirmed it has as active context
  // via the bind_ok WS message. This is backend-authoritative: the agent told us
  // what entity it's focused on. User can X to clear (sends unbind_work → agent releases scope).
  const agentContext = useMemo((): ActiveContext | null => {
    if (!workBinding || !workBinding.bound_id) return null
    const typeMap: Record<string, ContextCrumb["type"]> = {
      station: "station",
      inspector: "inspector",
      part: "part",
      training: "training",
    }
    const crumbType = typeMap[workBinding.bound_type] ?? "screen"
    return {
      crumbs: [
        {
          type: crumbType,
          label: workBinding.bound_label || workBinding.bound_id,
        },
      ],
      clearable: workBinding.source !== "entity",
    }
  }, [workBinding])

  const [pendingMentions, setPendingMentions] = useState<MentionTarget[]>([])

  // Effective context: backend bind_ok takes priority; fall back to pending
  // mention chips the user has selected but not yet sent.
  const effectiveContext = useMemo((): ActiveContext | null => {
    if (agentContext) return agentContext
    if (pendingMentions.length > 0) {
      const typeMap: Record<string, ContextCrumb["type"]> = {
        station: "station",
        inspector: "inspector",
        part: "part",
        training: "training",
      }
      return {
        crumbs: pendingMentions.slice(0, 3).map((m) => ({
          type: typeMap[m.type] ?? "screen",
          label: m.label,
        })),
        clearable: false,
      }
    }
    return null
  }, [agentContext, pendingMentions])

  const mentionResolversRef = useRef<
    Record<string, (items: MentionTarget[]) => void>
  >({})

  useEffect(() => {
    sessionIdRef.current = sessionId
  }, [sessionId])

  useEffect(() => {
    const container = containerNode
    if (!container) return

    const wideQuery =
      '.livis-ai-marker-wrapper[data-marker-type="chart"], .livis-ai-marker-wrapper[data-marker-type="table"], table, .recharts-wrapper, .highcharts-container, .livis-ai-markdown-image-wrapper, .livis-ai-big-summary'

    const checkVisibility = () => {
      if (!container) return
      const els = container.querySelectorAll(wideQuery)
      let isVisible = false
      const containerRect = container.getBoundingClientRect()

      els.forEach((el) => {
        const rect = el.getBoundingClientRect()
        // If element is completely above or completely below the container (with 200px margin), it's not visible
        const isAbove = rect.bottom < containerRect.top - 200
        const isBelow = rect.top > containerRect.bottom + 200
        if (!isAbove && !isBelow && rect.height > 0) {
          isVisible = true
        }
      })

      if (isWideModeRef.current !== isVisible) {
        isWideModeRef.current = isVisible
        setIsWideMode(isVisible)
      }
    }

    let timeoutId: any
    const debouncedCheck = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(checkVisibility, 50)
    }

    // Check on scroll
    container.addEventListener("scroll", debouncedCheck, { passive: true })

    // Check when DOM mutates
    const mutationObserver = new MutationObserver(() => {
      debouncedCheck()
    })
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "data-marker-type"],
    })

    // Fallback interval for lazy rendered elements (like Recharts measuring DOM)
    const intervalId = setInterval(debouncedCheck, 800)

    // Initial check
    debouncedCheck()

    return () => {
      container.removeEventListener("scroll", debouncedCheck)
      mutationObserver.disconnect()
      clearInterval(intervalId)
      clearTimeout(timeoutId)
      if (isWideModeRef.current) {
        isWideModeRef.current = false
        setIsWideMode(false)
      }
    }
  }, [containerNode]) // Fires every time the actual DOM node mounts/unmounts

  // When wide mode transitions, force charts to reflow so they don't appear squeezed
  useEffect(() => {
    // Dispatch during and after the CSS transition to ensure smooth reflow
    const t1 = setTimeout(() => window.dispatchEvent(new Event("resize")), 100)
    const t2 = setTimeout(() => window.dispatchEvent(new Event("resize")), 350)
    return () => {
      clearTimeout(t1)
      clearTimeout(t2)
    }
  }, [isWideMode])

  const resetChatActivityState = useCallback(() => {
    abortControllerRef.current?.abort()
    streamingMsgIdRef.current = null
    systemStreamMsgIdRef.current = null
    streamSessionRef.current = null
    setStreamPending(false)
    setIsLoading(false)
    setCurrentStatus("")
    setMessages((prev) =>
      prev.map((m) => (m.isLoading ? { ...m, isLoading: false } : m))
    )
  }, [setIsLoading, setCurrentStatus, setMessages])

  const finalizeStreamingMessage = useCallback(
    (msgId: string) => {
      const now = Date.now()
      setMessages((prev) =>
        prev
          .map((m) => {
            if (m.id !== msgId) return m
            if (!m.text && !m.markers?.length && !m.statuses?.length)
              return null as any
            return {
              ...m,
              isLoading: false,
              createdAt: m.createdAt || new Date().toISOString(),
              finishedAt: now,
            }
          })
          .filter(Boolean)
      )
    },
    [setMessages]
  )

  const activeStreamMsgId = useCallback((): string | null => {
    if (
      streamSessionRef.current &&
      streamSessionRef.current !== sessionIdRef.current
    ) {
      return null
    }
    return streamingMsgIdRef.current || systemStreamMsgIdRef.current
  }, [])

  const completeWsStreamTurn = useCallback(() => {
    if (
      streamSessionRef.current &&
      streamSessionRef.current !== sessionIdRef.current
    ) {
      return
    }
    if (systemStreamMsgIdRef.current) {
      finalizeStreamingMessage(systemStreamMsgIdRef.current)
      systemStreamMsgIdRef.current = null
    } else if (streamingMsgIdRef.current) {
      finalizeStreamingMessage(streamingMsgIdRef.current)
      streamingMsgIdRef.current = null
    }
    pendingEditRef.current = null
    if (!streamingMsgIdRef.current && !systemStreamMsgIdRef.current) {
      streamSessionRef.current = null
      setStreamPending(false)
      setIsLoading(false)
      setCurrentStatus("")
    }
  }, [finalizeStreamingMessage, setIsLoading, setCurrentStatus])

  // ── WebSocket hook ───────────────────────────────────────────────────────────
  const {
    isAuthOk: wsReady,
    connectionState: wsConnectionState,
    sendMessage: wsSend,
    sendEditMessage: wsSendEditMessage,
    sendEvent: wsSendEvent,
    sendSessionStart,
    sendCancel: wsSendCancel,
    requestMentionSuggestions,
    bindWork: wsBindWork,
    unbindWork: wsUnbindWork,
  } = useAgentWebSocket(sessionId || null, {
    onBindOk: (b) => {
      if (b.bound_type && b.bound_id) {
        setWorkBinding(b)
      } else {
        setWorkBinding(null)
      }
    },
    onMentionSuggestions: (requestId, items) => {
      const resolve = mentionResolversRef.current[requestId]
      if (!resolve) return
      delete mentionResolversRef.current[requestId]
      resolve((items || []) as MentionTarget[])
    },
    onMessagesTruncated: (fromId) => {
      if (!fromId) return
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === fromId)
        if (idx < 0) return prev
        const base = prev.slice(0, idx)
        const pending = pendingEditRef.current
        if (pending?.msgId === fromId) {
          return [
            ...base,
            {
              id: pending.msgId,
              type: "user" as const,
              text: pending.text,
              createdAt: new Date().toISOString(),
            },
            newAiPlaceholder(pending.aiMsgId),
          ]
        }
        return base
      })
    },
    onConnected: () => {
      /* WS ready */
    },
    onDisconnected: () => {
      /* WS reconnecting */
    },
    onStatus: (text) => {
      if (
        streamSessionRef.current &&
        streamSessionRef.current !== sessionIdRef.current
      )
        return
      setCurrentStatus(text)
      const id = streamingMsgIdRef.current || systemStreamMsgIdRef.current
      if (id && text) {
        const now = Date.now()
        const step = { text, ts: now }
        setMessages((prev) =>
          prev.map((m) => {
            if (m.id !== id) return m
            const patch: Partial<Message> = {
              steps: [...(m.steps || []), step],
            }
            // First backend frame — set startedAt for accurate elapsed (not send time)
            if (!m.startedAt) patch.startedAt = now
            return { ...m, ...patch }
          })
        )
      }
    },
    onToken: (token) => {
      const id = activeStreamMsgId()
      if (!id) return
      setMessages((prev) =>
        prev.map((m) =>
          m.id === id ? { ...m, text: m.text + token, isLoading: true } : m
        )
      )
    },
    onEnvelope: (envelope) => {
      const env = envelope as Envelope
      if (env.type === "refresh") {
        applyAgentSideEffects([env])
        return
      }
      const id = activeStreamMsgId()
      if (!id) return
      if (env.type === "agent_prose") {
        const chunk = env.payload?.text || ""
        if (!chunk) return
        setMessages((prev) => {
          const exists = prev.some((m) => m.id === id)
          if (!exists) return prev
          return prev.map((m) =>
            m.id === id
              ? { ...m, text: (m.text || "") + chunk, isLoading: true }
              : m
          )
        })
        return
      }
      setMessages((prev) => {
        const exists = prev.some((m) => m.id === id)
        if (!exists) return prev
        // action_status: new step marker — append to statuses list
        if ((envelope as any).type === "action_status") {
          const p = (envelope as any).payload || {}
          const s = {
            id: (envelope as any).id,
            state: p.state || "running",
            label: p.label || "",
            detail: p.detail,
            progress: p.progress,
          }
          return prev.map((m) =>
            m.id === id
              ? { ...m, statuses: [...(m.statuses || []), s], isLoading: true }
              : m
          )
        }
        // ui_update (update_action): update existing status by id
        if ((envelope as any).type === "ui_update") {
          const targetId = (envelope as any).id
          const patch = (envelope as any).payload || {}
          return prev.map((m) =>
            m.id === id
              ? {
                  ...m,
                  statuses: (m.statuses || []).map((s) =>
                    s.id === targetId ? { ...s, ...patch } : s
                  ),
                  markers: (m.markers || []).map((mk) =>
                    mk.id === targetId
                      ? { ...mk, payload: { ...(mk.payload || {}), ...patch } }
                      : mk
                  ),
                  isLoading: m.isLoading ?? true,
                }
              : m
          )
        }
        // Other envelopes: merge markers
        const updated = buildAiMessageFromEnvelopes(
          [envelope as Envelope],
          id,
          new Date().toISOString()
        )
        return prev.map((m) =>
          m.id === id
            ? {
                ...m,
                markers: [...(m.markers || []), ...(updated.markers || [])],
                statuses: [...(m.statuses || []), ...(updated.statuses || [])],
                isLoading: true,
              }
            : m
        )
      })
    },
    onDone: () => {
      completeWsStreamTurn()
    },
    onError: (detail) => {
      if (
        streamSessionRef.current &&
        streamSessionRef.current !== sessionIdRef.current
      ) {
        return
      }
      const id = streamingMsgIdRef.current || systemStreamMsgIdRef.current
      const errMsg: Message = {
        id: id || `ai-${Date.now()}`,
        type: "ai",
        text: detail || t("I'm sorry, something went wrong. Please try again.") as string,
        isLoading: false,
        createdAt: new Date().toISOString(),
      }
      setMessages((prev) => {
        if (id && prev.some((m) => m.id === id)) {
          return prev.map((m) =>
            m.id === id
              ? { ...errMsg, createdAt: m.createdAt || errMsg.createdAt }
              : m
          )
        }
        return [...prev, errMsg]
      })
      streamingMsgIdRef.current = null
      systemStreamMsgIdRef.current = null
      streamSessionRef.current = null
      setStreamPending(false)
      setIsLoading(false)
      setCurrentStatus("")
    },
    onPush: (data) => {
      // ui_update from monitor (progress bar tick) — patch the existing action_status marker
      if (data.type === "ui_update") {
        const targetId = data.id
        const patch = data.payload || {}
        setMessages((prev) =>
          prev.map((m) => {
            const hasStatus = (m.statuses || []).some(
              (s: any) => s.id === targetId
            )
            const hasMarker = (m.markers || []).some(
              (mk: any) => mk.id === targetId
            )
            if (!hasStatus && !hasMarker) return m
            return {
              ...m,
              statuses: (m.statuses || []).map((s: any) =>
                s.id === targetId ? { ...s, ...patch } : s
              ),
              markers: (m.markers || []).map((mk: any) =>
                mk.id === targetId
                  ? { ...mk, payload: { ...(mk.payload || {}), ...patch } }
                  : mk
              ),
            }
          })
        )
        return
      }
      // Monitor alert — inject as new AI message with action_menu
      const pushId = `push-${Date.now()}`
      const title = data.title || ""
      const body = data.body || ""
      const text = title ? `**${title}**\n\n${body}` : body
      const chips: Array<{ id: string; label: string }> = Array.isArray(
        data.chips
      )
        ? data.chips
        : []
      const markers: AgentMarkerData[] =
        chips.length > 0
          ? [
              {
                id: `${pushId}-am`,
                payload: {
                  marker_type: "action_menu",
                  actions: chips.map((c) => ({
                    id: c.id,
                    label: c.label,
                    description: "",
                    icon: "",
                  })),
                },
              },
            ]
          : []
      const pushMsg: Message = {
        id: pushId,
        type: "ai",
        text,
        isLoading: false,
        markers,
      }
      setMessages((prev) => [...prev, pushMsg])
    },
  })

  useEffect(() => {
    if (wsConnectionState === "online") {
      setShowConnBanner(false)
      return
    }
    const t = setTimeout(() => setShowConnBanner(true), 2000)
    return () => clearTimeout(t)
  }, [wsConnectionState])

  useEffect(() => {
    if (!wsReady || !sessionId) return
    const b = getCopilotWorkBinding()
    const nextSig = b ? `${b.bound_type}:${b.bound_id}` : ""
    const curSig = workBinding?.bound_id
      ? `${workBinding.bound_type}:${workBinding.bound_id}`
      : ""
    if (nextSig === curSig) return
    if (b) {
      wsBindWork(b.bound_type, b.bound_id, b.bound_label)
    } else {
      wsUnbindWork()
      setWorkBinding(null)
    }
  }, [
    wsReady,
    sessionId,
    location.pathname,
    location.search,
    routePageContext.screen,
    wsBindWork,
    wsUnbindWork,
  ])

  const searchMentions = useCallback(
    (query: string): Promise<MentionTarget[]> => {
      if (!wsReady) return Promise.resolve([])
      const requestId = `mention-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`
      return new Promise((resolve) => {
        mentionResolversRef.current[requestId] = resolve
        const sent = requestMentionSuggestions(query, requestId)
        if (!sent) {
          delete mentionResolversRef.current[requestId]
          resolve([])
          return
        }
        window.setTimeout(() => {
          if (mentionResolversRef.current[requestId]) {
            delete mentionResolversRef.current[requestId]
            resolve([])
          }
        }, 4000)
      })
    },
    [requestMentionSuggestions, wsReady]
  )

  const isSessionStarting =
    !!systemStreamMsgIdRef.current || shouldSendSessionStartRef.current
  const isWsConnecting = wsConnectionState !== "online"
  const inputLocked =
    isLoading || streamPending || isSessionStarting || isWsConnecting

  // Switching to Ask mode releases the entity context — Ask is unscoped Q&A.
  useEffect(() => {
    if (inputMode === "ask") {
      wsUnbindWork()
      setWorkBinding(null)
    }
  }, [inputMode, wsUnbindWork])

  const requestSessionStart = useCallback(() => {
    shouldSendSessionStartRef.current = true
    if (wsReady && sendSessionStart()) {
      shouldSendSessionStartRef.current = false
    }
  }, [wsReady, sendSessionStart])

  // Emit session_start when WS becomes ready (or immediately if already connected)
  useEffect(() => {
    if (wsReady && shouldSendSessionStartRef.current && sendSessionStart()) {
      shouldSendSessionStartRef.current = false
    }
  }, [wsReady, sendSessionStart])

  /** Empty session — show blank chat, no greeting. session_start sent silently on first message. */
  const beginEmptySessionGreeting = useCallback(
    (targetSessionId: string) => {
      systemStreamMsgIdRef.current = null
      streamSessionRef.current = targetSessionId
      setStreamPending(false)
      setCurrentStatus("")
      setMessages([])
      // Queue session_start so backend initialises state; it emits nothing visible.
      requestSessionStart()
    },
    [requestSessionStart, setMessages, setCurrentStatus]
  )

  // Persist tab and view state — restore to messages/chat if user was mid-conversation
  const [activeTab, setActiveTab] = useState<
    "home" | "messages" | "help" | "news" | "alerts"
  >(() => {
    const stored = sessionStorage.getItem("livis_chat_tab") as
      | "home"
      | "messages"
      | "help"
      | "news"
      | "alerts"
      | null
    return stored || "messages"
  })
  const [activeView, setActiveView] = useState<"list" | "chat">(() => {
    return (
      (sessionStorage.getItem("livis_chat_view") as "list" | "chat" | null) ||
      "list"
    )
  })
  // Tracks the current LIVIS screen context from backend responses

  const [sessions, setSessions] = useState<SessionSummary[]>([])
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState("")
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null)
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(
    null
  )
  // Use mode === "fullscreen" as isExpanded
  const isExpanded = mode === "fullscreen"
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false)
  const shouldScrollRef = useRef(true)
  const lastScrolledMsgIdRef = useRef<string | null>(null)

  // Alerts and Help State
  const [alerts, setAlerts] = useState<any[]>([])
  const [helpSearchQuery, setHelpSearchQuery] = useState("")

  // Persist drill-down states for Help tab
  const [selectedHelpCategory, setSelectedHelpCategory] = useState<any>(() => {
    try {
      const stored = localStorage.getItem("livis_ai_selected_help_category")
      return stored ? JSON.parse(stored) : null
    } catch {
      return null
    }
  })
  const [selectedHelpPath, setSelectedHelpPath] = useState<string | null>(
    () => {
      return localStorage.getItem("livis_ai_selected_help_path") || null
    }
  )

  // Persist nav tab and view to sessionStorage
  useEffect(() => {
    sessionStorage.setItem("livis_chat_tab", activeTab)
  }, [activeTab])
  useEffect(() => {
    sessionStorage.setItem("livis_chat_view", activeView)
  }, [activeView])

  useEffect(() => {
    if (selectedHelpCategory) {
      localStorage.setItem(
        "livis_ai_selected_help_category",
        JSON.stringify(selectedHelpCategory)
      )
    } else {
      localStorage.removeItem("livis_ai_selected_help_category")
    }
  }, [selectedHelpCategory])

  useEffect(() => {
    if (selectedHelpPath) {
      localStorage.setItem("livis_ai_selected_help_path", selectedHelpPath)
    } else {
      localStorage.removeItem("livis_ai_selected_help_path")
    }
  }, [selectedHelpPath])

  useEffect(() => {
    if (activeTab === "messages" && activeView === "list") {
      loadSessions(true)
    }
  }, [activeTab, activeView])

  // Lower the AI chat wrapper z-index when the delete modal is open so LivisModal
  // (MUI Dialog, z-index 1300) renders above the wrapper (z-index 999999).
  useEffect(() => {
    if (deleteConfirmOpen) {
      document.body.classList.add("livis-ai-modal-open")
    } else {
      document.body.classList.remove("livis-ai-modal-open")
    }
    return () => {
      document.body.classList.remove("livis-ai-modal-open")
    }
  }, [deleteConfirmOpen])

  useEffect(() => {
    if (activeTab === "alerts") {
      getAllAlertsAPI({ sort: -1 })
        .then((res) => {
          const resBody = res?.data
          let list = []
          if (Array.isArray(resBody?.data?.notifications))
            list = resBody.data.notifications
          else if (Array.isArray(resBody?.notifications))
            list = resBody.notifications
          else if (Array.isArray(resBody?.data?.results))
            list = resBody.data.results
          else if (Array.isArray(resBody?.results)) list = resBody.results
          else if (Array.isArray(resBody?.data)) list = resBody.data
          else if (Array.isArray(resBody)) list = resBody
          setAlerts(list)
        })
        .catch((err) => console.error("Failed to fetch alerts", err))
    }
  }, [activeTab])

  // Pagination State
  const [sessionsOffset, setSessionsOffset] = useState(0)
  const [hasMoreSessions, setHasMoreSessions] = useState(true)
  const [messagesOffset, setMessagesOffset] = useState(0)
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const PAGE_SIZE = 50

  const { t } = useTranslation()

  const isInitialMount = useRef(true)

  const lastMsgContentSigRef = useRef<string>("")

  const scrollMessageToStart = useCallback((targetId: string) => {
    const container = messagesContainerRef.current
    if (!container || !targetId) return
    const anchor = container.querySelector(
      `[data-message-id="${CSS.escape(targetId)}"]`
    ) as HTMLElement | null
    if (!anchor) return
    const pad = 12
    const cRect = container.getBoundingClientRect()
    const top =
      anchor.getBoundingClientRect().top - cRect.top + container.scrollTop
    container.scrollTop = Math.max(0, top - pad)
  }, [])

  const isChatNearBottom = useCallback((container: HTMLElement) => {
    const threshold = 140
    return (
      container.scrollHeight - container.scrollTop - container.clientHeight <=
      threshold
    )
  }, [])

  /** ChatGPT-style: new turn shows the start of the reply; stream follow only if already at bottom. */
  const scrollToNewTurn = useCallback(
    (msgId: string, opts?: { followStream?: boolean }) => {
      if (!shouldScrollRef.current || !msgId) return
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const container = messagesContainerRef.current
          if (!container) return
          const list = messagesRef.current
          const idx = list.findIndex((m) => m.id === msgId)

          if (opts?.followStream) {
            if (!isChatNearBottom(container)) return
            container.scrollTop = container.scrollHeight
            return
          }

          scrollMessageToStart(msgId)
        })
      })
    },
    [scrollMessageToStart, isChatNearBottom]
  )

  useEffect(() => {
    if (!shouldScrollRef.current || !messages?.length) return
    const last = messages[messages.length - 1]
    if (!last?.id) return
    const sig = `${last.id}:${(last.text || "").length}:${
      last.markers?.length || 0
    }`
    const idChanged = lastScrolledMsgIdRef.current !== last.id
    const contentGrew =
      !idChanged && last.type === "ai" && sig !== lastMsgContentSigRef.current
    if (!idChanged && !contentGrew) return
    lastScrolledMsgIdRef.current = last.id
    lastMsgContentSigRef.current = sig
    const followStream = contentGrew && !!last.isLoading
    scrollToNewTurn(last.id, followStream ? { followStream: true } : undefined)
    if (isInitialMount.current) {
      setTimeout(() => {
        isInitialMount.current = false
      }, 50)
    }
  }, [messages, scrollToNewTurn])

  // ── Session history ─────────────────────────────────────────────────────────
  const loadSessions = useCallback(
    async (reset = false) => {
      try {
        const newOffset = reset ? 0 : sessionsOffset
        const response = await getSessions(newOffset, PAGE_SIZE)
        const json = response?.data
        const sessionData = json?.data?.sessions
        const total = json?.data?.total || 0

        if (sessionData && Array.isArray(sessionData)) {
          const mapped = sessionData.map((s: any) => ({
            ...s,
            summary: s?.title || s?.summary || "New Chat",
          }))

          const myEmail = (resolveUserEmail() || "").toLowerCase()
          const filtered = myEmail
            ? mapped.filter(
                (s: any) =>
                  !s.user_email ||
                  String(s.user_email).toLowerCase() === myEmail
              )
            : mapped

          if (reset) {
            setSessions(filtered)
            setSessionsOffset(mapped.length)
          } else {
            setSessions((prev) => {
              const newMap = new Map(prev.map((s) => [s.session_id, s]))
              filtered.forEach((s: any) => newMap.set(s.session_id, s))
              return Array.from(newMap.values())
            })
            setSessionsOffset((prev) => prev + mapped.length)
          }

          // Use total from API to determine if more exist
          setHasMoreSessions(
            (reset ? filtered.length : sessions.length + filtered.length) <
              total
          )
        } else {
          if (reset) setSessions([])
          setHasMoreSessions(false)
        }
      } catch (err) {
        console.error("Failed to load sessions", err)
        toast.error(t("Could not load chat history"))
      }
    },
    [sessionsOffset, sessions.length]
  )

  useEffect(() => {
    loadSessions(true)
  }, []) // Load sessions initially so Home tab has recent message

  const titlePollCountRef = useRef(0)

  useEffect(() => {
    // When stream and loading finishes, check if we need to reload sessions
    // to update chat history sidebar and chat title for a new chat
    if (!streamPending && !isLoading && sessionId && messages.length > 0) {
      const currentSession = sessions.find((s) => s.session_id === sessionId)
      const isNewChat =
        !currentSession ||
        currentSession.summary === "New Chat" ||
        currentSession.summary === "New chat"

      if (isNewChat) {
        // The backend might take a moment to generate the title asynchronously.
        // Poll up to 5 times (every 2 seconds) to get the updated summary.
        if (titlePollCountRef.current < 5) {
          const timer = setTimeout(() => {
            titlePollCountRef.current += 1
            loadSessions(true)
          }, 2000)
          return () => clearTimeout(timer)
        }
      } else {
        // Successfully loaded real title
        titlePollCountRef.current = 0
      }
    } else if (streamPending || isLoading) {
      // Reset counter when a new stream starts
      titlePollCountRef.current = 0
    }
  }, [
    streamPending,
    isLoading,
    sessionId,
    messages.length,
    sessions,
    loadSessions,
  ])

  const handleSessionSelect = async (sessId: string) => {
    if (
      sessId === sessionId &&
      messages.length > 0 &&
      !isLoading &&
      !streamPending
    ) {
      return
    }
    const loadGen = ++sessionLoadGenRef.current
    // Cancel any in-flight backend turn before switching — stops wasted LLM work and
    // prevents the old session's done/error frame from landing in the new session.
    if (streamPending || isLoading) wsSendCancel()
    resetChatActivityState()
    setMessages([])
    setSessionId(sessId)
    setMessagesOffset(0)
    setHasMoreMessages(false)
    try {
      setIsLoading(true)
      shouldScrollRef.current = true
      const response = await getSession(sessId, 0, PAGE_SIZE)
      if (loadGen !== sessionLoadGenRef.current) return
      const json = response?.data
      const messageData = json?.data?.messages
      const total = json?.data?.total_messages

      if (messageData && Array.isArray(messageData)) {
        const rawCount = messageData.length
        const formatted = formatHistoryMessages(messageData)

        const lastAiIdx = formatted.reduce(
          (last, m, idx) => (m.type === "ai" ? idx : last),
          -1
        )
        const withDisabled = formatted.map((m, idx) =>
          m.type === "ai" && idx !== lastAiIdx
            ? {
                ...m,
                markers: (m.markers || []).map((x) => ({
                  ...x,
                  disabled: true,
                })),
              }
            : m
        )

        if (withDisabled.length > 0) {
          // Disable scroll-to-turn logic — for history loads we just snap to bottom
          shouldScrollRef.current = false
          setMessages(withDisabled)
          // Snap to bottom twice: once after render (50ms) and once after the
          // Zoom open animation completes (350ms), so history is always visible
          // regardless of whether the chat window was still animating open.
          const snapToBottom = () => {
            const container = messagesContainerRef.current
            if (container) container.scrollTop = container.scrollHeight
          }
          setTimeout(snapToBottom, 50)
          setTimeout(snapToBottom, 350)
        } else if (typeof total === "number" && total > 0) {
          toast.error(t("Could not load this conversation. Please try again."))
          setMessages([])
        } else {
          // Cleared or fresh session — greeting lives only in WS, not in Qdrant history
          beginEmptySessionGreeting(sessId)
        }
        setMessagesOffset(rawCount)

        if (typeof total === "number") {
          setHasMoreMessages(rawCount < total)
        } else {
          setHasMoreMessages(rawCount === PAGE_SIZE)
        }
      }
    } catch {
      if (loadGen !== sessionLoadGenRef.current) return
      toast.error(t("Failed to load session history"))
    } finally {
      if (loadGen === sessionLoadGenRef.current) {
        setIsLoading(false)
      }
    }
  }

  // When sessionId is restored (refresh / reopen) but messages are empty, reload from API.
  useEffect(() => {
    if (!sessionId || messages.length > 0) return
    if (autoRestoredSessionsRef.current.has(sessionId)) return
    autoRestoredSessionsRef.current.add(sessionId)
    void handleSessionSelect(sessionId)
  }, [sessionId, messages.length])

  const loadMoreMessages = async () => {
    if (!sessionId || !hasMoreMessages || isLoading) return
    try {
      setIsLoading(true)
      shouldScrollRef.current = false // Prevent auto-scroll when loading history
      lastScrolledMsgIdRef.current = null
      const response = await getSession(sessionId, messagesOffset, PAGE_SIZE)
      const json = response?.data
      const messageData = json?.data?.messages
      const total = json?.data?.total_messages

      if (messageData && Array.isArray(messageData)) {
        const rawCount = messageData.length
        const formatted = formatHistoryMessages(messageData)
        // All prepended (older) messages must have disabled markers
        const withDisabled = formatted.map((m) =>
          m.type === "ai"
            ? {
                ...m,
                markers: (m.markers || []).map((x) => ({
                  ...x,
                  disabled: true,
                })),
              }
            : m
        )
        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id))
          const uniqueFormatted = withDisabled.filter(
            (m) => !existingIds.has(m.id)
          )
          return [...uniqueFormatted, ...prev]
        })
        const newOffset = messagesOffset + rawCount // raw count keeps API pagination correct
        setMessagesOffset(newOffset)

        if (typeof total === "number") {
          setHasMoreMessages(newOffset < total)
        } else {
          setHasMoreMessages(rawCount === PAGE_SIZE)
        }
      }
    } catch {
      toast.error(t("Failed to load older messages"))
    } finally {
      setIsLoading(false)
    }
  }

  const handleRenameSession = async (sessId: string, newSummary: string) => {
    try {
      const response = await renameSession(sessId, newSummary)
      if (response?.status === 200) {
        setSessions(
          (prev) =>
            prev?.map((s) =>
              s?.session_id === sessId ? { ...s, summary: newSummary } : s
            ) || []
        )
        setEditingSessionId(null)
      } else {
        livisAlert(t("Failed to rename conversation") as string, "error")
      }
    } catch {
      livisAlert(t("Failed to rename conversation") as string, "error")
    }
  }

  const handleDeleteSession = (e: React.MouseEvent, sessId: string) => {
    e.stopPropagation()
    document.body.classList.add("livis-ai-modal-open")
    setSessionToDelete(sessId)
    setDeleteConfirmOpen(true)
  }

  const confirmDelete = async () => {
    if (!sessionToDelete) return
    try {
      const res = await deleteSession(sessionToDelete)
      if (res?.status === 200) {
        setSessions(
          (prev) => prev?.filter((s) => s?.session_id !== sessionToDelete) || []
        )
        if (sessionToDelete === sessionId) {
          setSessionId("")
          setMessages([
            {
              id: Date.now().toString(),
              type: "ai",
              text: t("Conversation deleted. Ready for a new chat.") as string,
            },
          ])
        }
      }
    } catch {
      toast.error(t("Delete failed"))
    } finally {
      setDeleteConfirmOpen(false)
      setSessionToDelete(null)
    }
  }

  // ── REST-only messaging (no WebSocket) ─────────────────────────────────────

  /** Process a list of agent envelopes and append one AI message. */
  const processEnvelopes = useCallback(
    (envelopes: Envelope[], aiMsgId: string) => {
      if (envelopes.length > 0) playChatSound("receive")
      const aiMsg = buildAiMessageFromEnvelopes(
        envelopes,
        aiMsgId,
        new Date().toISOString()
      )

      setMessages((prev) => {
        const exists = prev?.some((m) => m?.id === aiMsgId)
        const next = { ...aiMsg, isLoading: false }
        if (!next.text && !next.markers?.length && !next.statuses?.length) {
          return exists ? prev.filter((m) => m.id !== aiMsgId) : prev
        }
        return exists
          ? prev.map((m) => (m.id === aiMsgId ? next : m))
          : [...(prev || []), next]
      })

      applyAgentSideEffects(envelopes)
    },
    [setMessages]
  )

  // Keep a ref to messages so handleMarkerAction can read current messages without stale closure
  const messagesRef = useRef<Message[]>([])
  const pendingEditRef = useRef<{
    msgId: string
    text: string
    aiMsgId: string
  } | null>(null)
  useEffect(() => {
    messagesRef.current = messages || []
  }, [messages])

  const lastEditableUserMsgId = useMemo(() => {
    const list = messages || []
    for (let i = list.length - 1; i >= 0; i -= 1) {
      const m = list[i]
      if (m?.type === "user" && (m.text || "").trim()) return m.id
    }
    return null
  }, [messages])

  const lastRegenerableAiMsgId = useMemo(() => {
    const list = messages || []
    for (let i = list.length - 1; i >= 0; i -= 1) {
      if (isRegenerableAiMessage(list[i])) return list[i].id
    }
    return null
  }, [messages])

  const handleUploadComplete = useCallback(
    async (fileRef: string, kind: string, context: any) => {
      if (!sessionId) return
      setIsLoading(true)
      setCurrentStatus(t("Reading uploaded file...") as string)
      const aiId = `ai-${Date.now()}`
      try {
        const resp = await sendAgentEvent(sessionId, null, "upload_complete", {
          file_ref: fileRef,
          kind,
          context,
        })
        const envelopes: Envelope[] = resp?.data?.data?.envelopes || []
        processEnvelopes(envelopes, aiId)
      } catch (err) {
        console.error("Upload complete error:", err)
      } finally {
        setIsLoading(false)
        setCurrentStatus("")
      }
    },
    [sessionId, setIsLoading, setCurrentStatus, processEnvelopes]
  )

  // ── Messaging ───────────────────────────────────────────────────────────────

  const cancelStream = useCallback(() => {
    wsSendCancel()
    resetChatActivityState()
  }, [resetChatActivityState, wsSendCancel])

  const pushUserBubble = useCallback(
    (text: string) => {
      const trimmed = text.trim()
      if (!trimmed) return
      shouldScrollRef.current = true
      const userMsg: Message = {
        id: `user-${Date.now()}`,
        type: "user",
        text: trimmed,
        createdAt: new Date().toISOString(),
      }
      setMessages((prev) => [...(prev || []), userMsg])
      playChatSound("send")
    },
    [setMessages]
  )

  const sendMessage = useCallback(
    async (
      text: string,
      modeOverride?: "agent" | "ask",
      sendOpts?: {
        regenerate?: boolean
        skipUserMessage?: boolean
        message_id?: string
        regenerate_from_message_id?: string
        regenerate_goal?: string
        mentions?: MentionTarget[]
      }
    ) => {
      if (!text.trim() || text.length > MAX_QUERY_LENGTH) return
      if (streamPending || isLoading) {
        wsSendCancel()
        resetChatActivityState()
      }

      // Disable all prior interactive markers so stale forms can't be re-submitted
      setMessages((prev) =>
        prev?.map((m) =>
          m?.type === "ai"
            ? {
                ...m,
                markers: (m.markers || []).map((x) => ({
                  ...x,
                  disabled: true,
                })),
              }
            : m
        )
      )

      shouldScrollRef.current = true
      const userMsg: Message = {
        id: Date.now().toString(),
        type: "user",
        text,
        createdAt: new Date().toISOString(),
      }
      if (!sendOpts?.skipUserMessage) {
        setMessages((prev) => [...(prev || []), userMsg])
      }
      setIsLoading(true)
      setCurrentStatus(t("Thinking...") as string)
      playChatSound("send")

      const aiMsgId = `ai-${Date.now()}`

      // ── WebSocket path (agent + ask) ─────────────────────────────────────────
      if (wsReady && sessionId) {
        // User message supersedes an in-flight session_start placeholder
        if (systemStreamMsgIdRef.current) {
          const sysId = systemStreamMsgIdRef.current
          systemStreamMsgIdRef.current = null
          setMessages((prev) =>
            prev.filter(
              (m) => m.id !== sysId || m.text || (m.markers?.length ?? 0) > 0
            )
          )
        }
        streamingMsgIdRef.current = aiMsgId
        streamSessionRef.current = sessionId
        setStreamPending(true)
        setMessages((prev) => withSingleLoadingPlaceholder(prev || [], aiMsgId))
        const wsMsgId =
          sendOpts?.message_id ||
          (!sendOpts?.skipUserMessage ? userMsg.id : undefined)
        const mentions = sendOpts?.mentions || []
        if (mentions.length) {
          const primary = mentions[0]
          wsBindWork(primary.type, primary.id, primary.label)
        }
        const sent = wsSend(text, modeOverride ?? inputMode, {
          ...(mentions.length ? { mentions } : {}),
          ...(sendOpts?.regenerate ? { regenerate: true } : {}),
          ...(wsMsgId ? { message_id: wsMsgId } : {}),
          ...(sendOpts?.regenerate_from_message_id
            ? {
                regenerate_from_message_id: sendOpts.regenerate_from_message_id,
              }
            : {}),
          ...(sendOpts?.regenerate_goal
            ? { regenerate_goal: sendOpts.regenerate_goal }
            : {}),
        })
        if (sent) {
          loadSessions()
          return
        }
        streamingMsgIdRef.current = null
        streamSessionRef.current = null
        setStreamPending(false)
        setMessages((prev) => prev.filter((m) => m.id !== aiMsgId))
      }

      // ── REST fallback when WS is not connected or send failed ─────────────────
      const restPlaceholderExists = (prev: Message[]) =>
        prev.some((m) => m.id === aiMsgId)
      setMessages((prev) =>
        restPlaceholderExists(prev)
          ? prev
          : withSingleLoadingPlaceholder(prev || [], aiMsgId)
      )
      try {
        let currentSessionId = sessionId

        if (!currentSessionId) {
          const response = await createSession(
            text.substring(0, 50) || "New Chat"
          )
          const createJson = response?.data
          if (createJson?.data?.session_id) {
            currentSessionId = createJson.data.session_id
            setSessionId(currentSessionId)
          } else {
            throw new Error("Session ID not received from server")
          }
        }

        abortControllerRef.current = new AbortController()
        const response = await askRag(
          currentSessionId,
          text,
          modeOverride ?? inputMode,
          abortControllerRef.current?.signal,
          {
            regenerate: sendOpts?.regenerate,
            ui_context: getCopilotUiContext(),
            mentions: sendOpts?.mentions,
          }
        )
        if (abortControllerRef.current?.signal.aborted) return

        if (response?.status !== 200) {
          throw new Error(
            response?.data?.message || "Error communicating with AI"
          )
        }

        const envelopes: Envelope[] = response?.data?.data?.envelopes || []
        processEnvelopes(envelopes, aiMsgId)
        loadSessions()
      } catch (error: any) {
        if (
          error?.name === "AbortError" ||
          error?.name === "CanceledError" ||
          error?.code === "ERR_CANCELED" ||
          error?.message === "canceled" ||
          error?.config?.signal?.aborted
        )
          return
        console.error("Chat error:", error)

        const apiMsg = error?.response?.data?.message
        toast.error(
          apiMsg ||
            t("An error occurred while communicating with LIVIS Copilot")
        )

        setMessages((prev) => {
          const exists = prev?.some((m) => m?.id === aiMsgId)
          const errMsg: Message = {
            id: aiMsgId,
            type: "ai",
            text:
              apiMsg ||
              t("I'm sorry, I encountered an error. Please try again.") as string,
            isLoading: false,
            createdAt: new Date().toISOString(),
          }
          return exists
            ? prev?.map((m) => (m?.id === aiMsgId ? errMsg : m))
            : [...(prev || []), errMsg]
        })
      } finally {
        setIsLoading(false)
        setCurrentStatus("")
      }
    },
    [
      isLoading,
      streamPending,
      inputMode,
      sessionId,
      wsReady,
      wsSend,
      wsSendCancel,
      resetChatActivityState,
      setMessages,
      setSessionId,
      setIsLoading,
      setCurrentStatus,
      processEnvelopes,
      loadSessions,
    ]
  )

  const handleMessageFeedback = useCallback(
    async (msgId: string, rating: "up" | "down") => {
      if (!sessionId) return
      const existing = messages.find((m) => m.id === msgId)
      if (existing?.feedbackRecorded) return
      setMessages((prev) =>
        prev.map((m) => (m.id === msgId ? { ...m, feedbackRating: rating } : m))
      )
      try {
        await postAgentFeedback(sessionId, rating, msgId)
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? { ...m, feedbackRating: rating, feedbackRecorded: true }
              : m
          )
        )
      } catch {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId
              ? { ...m, feedbackRating: undefined, feedbackRecorded: false }
              : m
          )
        )
        toast.error(t("Could not save feedback"))
      }
    },
    [sessionId, setMessages, messages]
  )

  const handleEditUserMessage = useCallback(
    (msgId: string, newText: string) => {
      const trimmed = newText.trim()
      if (!trimmed || isLoading || streamPending) return
      setEditingUserMsgId(null)
      setEditUserDraft("")
      setIsLoading(true)
      setCurrentStatus(t("Thinking...") as string)
      shouldScrollRef.current = true
      const aiMsgId = `ai-${Date.now()}`
      pendingEditRef.current = { msgId, text: trimmed, aiMsgId }
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === msgId)
        if (idx < 0) return prev
        const userMsg: Message = {
          id: msgId,
          type: "user",
          text: trimmed,
          createdAt: new Date().toISOString(),
        }
        return [...prev.slice(0, idx), userMsg, newAiPlaceholder(aiMsgId)]
      })
      playChatSound("send")
      if (wsReady && sessionId) {
        streamingMsgIdRef.current = aiMsgId
        streamSessionRef.current = sessionId
        setStreamPending(true)
        const sent = wsSendEditMessage(msgId, trimmed, inputMode)
        if (!sent) {
          void sendMessage(trimmed, inputMode, {
            skipUserMessage: true,
            message_id: msgId,
          })
        }
        return
      }
      void sendMessage(trimmed, inputMode, {
        skipUserMessage: true,
        message_id: msgId,
      })
    },
    [
      isLoading,
      streamPending,
      sessionId,
      wsReady,
      wsSendEditMessage,
      inputMode,
      sendMessage,
      setMessages,
      setIsLoading,
      setCurrentStatus,
      setStreamPending,
    ]
  )

  const handleRegenerateMessage = useCallback(
    (msgId: string) => {
      const list = messagesRef.current || []
      const idx = list.findIndex((m) => m.id === msgId)
      if (idx < 0) return
      if (!isRegenerableAiMessage(list[idx])) return
      const regenGoal = inferRegenerateGoal(list[idx])
      const prev = idx > 0 ? list[idx - 1] : undefined
      let lastUser = ""
      if (prev?.type === "user" && (prev.text || "").trim()) {
        lastUser = prev.text!.trim()
      } else if (regenGoal === "analyze_data") {
        lastUser =
          "Re-run dataset analysis for the current part with live counts."
      } else {
        for (let i = idx - 1; i >= 0; i -= 1) {
          if (list[i]?.type === "user" && (list[i]?.text || "").trim()) {
            lastUser = (list[i]?.text || "").trim()
            break
          }
        }
      }
      if (!lastUser || isLoading || streamPending) return
      setMessages((prev) => {
        const cutAt = prev.findIndex((m) => m.id === msgId)
        if (cutAt < 0) return prev
        return prev.slice(0, cutAt)
      })
      if (wsReady && sessionId) {
        setIsLoading(true)
        setCurrentStatus(t("Thinking...") as string)
        const aiMsgId = `ai-${Date.now()}`
        streamingMsgIdRef.current = aiMsgId
        streamSessionRef.current = sessionId
        setStreamPending(true)
        setMessages((prev) => withSingleLoadingPlaceholder(prev || [], aiMsgId))
        const wsOpts = {
          regenerate: true,
          regenerate_from_message_id: msgId,
          ...(regenGoal ? { regenerate_goal: regenGoal } : {}),
        }
        const sent = wsSend(lastUser, inputMode, wsOpts)
        if (!sent) {
          void sendMessage(lastUser, inputMode, {
            skipUserMessage: true,
            ...wsOpts,
          })
        }
        return
      }
      void sendMessage(lastUser, inputMode, {
        skipUserMessage: true,
        regenerate: true,
        regenerate_from_message_id: msgId,
        ...(regenGoal ? { regenerate_goal: regenGoal } : {}),
      })
    },
    [
      isLoading,
      streamPending,
      sessionId,
      wsReady,
      wsSend,
      inputMode,
      sendMessage,
      setMessages,
      setIsLoading,
      setCurrentStatus,
      setStreamPending,
    ]
  )

  const handleMarkerAction = useCallback(
    async (aiMsgId: string, markerId: string, action: string, value: any) => {
      if (!sessionId || isLoading || streamPending) return

      if (action === "quick_reply" && typeof value === "object") {
        const chipId = String(value?.id || "")
        const chipLabel = String(value?.label || "").trim()
        if (chipId === "ask_question" && chipLabel) {
          sendMessage(chipLabel, "agent")
          return
        }
        if (chipId && COPILOT_ACTION_CHIP_IDS.has(chipId)) {
          const bubbleText = chipLabel || chipId.replace(/_/g, " ")
          pushUserBubble(bubbleText)
          setMessages((prev) =>
            prev?.map((m) =>
              m?.type === "ai"
                ? {
                    ...m,
                    markers: (m.markers || []).map((x) => ({
                      ...x,
                      disabled: true,
                    })),
                  }
                : m
            )
          )
          setIsLoading(true)
          setCurrentStatus(t("Thinking...") as string)
          const aiId = `ai-${Date.now()}`
          if (wsReady) {
            if (systemStreamMsgIdRef.current) {
              const sysId = systemStreamMsgIdRef.current
              systemStreamMsgIdRef.current = null
              setMessages((prev) =>
                prev.filter(
                  (m) =>
                    m.id !== sysId || m.text || (m.markers?.length ?? 0) > 0
                )
              )
            }
            streamingMsgIdRef.current = aiId
            streamSessionRef.current = sessionId
            setStreamPending(true)
            setMessages((prev) =>
              withSingleLoadingPlaceholder(prev || [], aiId)
            )
            const sent = wsSendEvent(
              "quick_reply",
              { id: chipId, label: chipLabel || chipId },
              markerId ?? undefined
            )
            if (sent) {
              loadSessions()
              return
            }
            streamingMsgIdRef.current = null
            streamSessionRef.current = null
            setStreamPending(false)
            setMessages((prev) => prev.filter((m) => m.id !== aiId))
          } else {
            setMessages((prev) =>
              withSingleLoadingPlaceholder(prev || [], aiId)
            )
          }
          abortControllerRef.current = new AbortController()
          try {
            const resp = await sendAgentEvent(
              sessionId,
              markerId,
              "quick_reply",
              { id: chipId, label: chipLabel || chipId },
              abortControllerRef.current.signal
            )
            if (abortControllerRef.current.signal.aborted) return
            processEnvelopes(resp?.data?.data?.envelopes || [], aiId)
            loadSessions()
          } catch (err: any) {
            if (
              err?.name === "AbortError" ||
              err?.name === "CanceledError" ||
              err?.code === "ERR_CANCELED"
            )
              return
            setMessages((prev) => [
              ...(prev || []),
              {
                id: aiId,
                type: "ai",
                text:
                  err?.response?.data?.detail ||
                  t("Something went wrong running that action.") as string,
                isLoading: false,
                createdAt: new Date().toISOString(),
              },
            ])
          } finally {
            setIsLoading(false)
            setCurrentStatus("")
          }
          return
        }
        if (chipLabel) {
          sendMessage(chipLabel, "agent")
          return
        }
      }

      // Disable all prior markers; persist what the user submitted on this marker
      setMessages((prev) =>
        prev?.map((m) =>
          m?.type === "ai"
            ? {
                ...m,
                markers: disableMarkersWithSubmission(
                  m.markers,
                  markerId,
                  action,
                  value
                ),
              }
            : m
        )
      )

      setIsLoading(true)
      setCurrentStatus(t("Thinking...") as string)
      const aiId = `ai-${Date.now()}`

      // ── WebSocket path for events ────────────────────────────────────────────
      if (wsReady) {
        if (systemStreamMsgIdRef.current) {
          const sysId = systemStreamMsgIdRef.current
          systemStreamMsgIdRef.current = null
          setMessages((prev) =>
            prev.filter(
              (m) => m.id !== sysId || m.text || (m.markers?.length ?? 0) > 0
            )
          )
        }
        streamingMsgIdRef.current = aiId
        streamSessionRef.current = sessionId
        setStreamPending(true)
        setMessages((prev) => withSingleLoadingPlaceholder(prev || [], aiId))
        const sent = wsSendEvent(action, value, markerId ?? undefined)
        if (sent) {
          loadSessions()
          return
        }
        streamingMsgIdRef.current = null
        streamSessionRef.current = null
        setStreamPending(false)
        setMessages((prev) => prev.filter((m) => m.id !== aiId))
      }

      // ── REST fallback ────────────────────────────────────────────────────────
      abortControllerRef.current = new AbortController()
      try {
        const resp = await sendAgentEvent(
          sessionId,
          markerId,
          action,
          value,
          abortControllerRef.current.signal
        )
        if (abortControllerRef.current.signal.aborted) return
        const envelopes: Envelope[] = resp?.data?.data?.envelopes || []
        processEnvelopes(envelopes, aiId)
        loadSessions()
      } catch (err: any) {
        if (
          err?.name === "AbortError" ||
          err?.name === "CanceledError" ||
          err?.code === "ERR_CANCELED"
        )
          return
        console.error("Event error:", err)
        const apiMsg =
          err?.response?.data?.detail || err?.response?.data?.message
        setMessages((prev) => {
          const errMsg: Message = {
            id: aiId,
            type: "ai",
            text:
              apiMsg || "I'm sorry, something went wrong. Please try again.",
            isLoading: false,
            createdAt: new Date().toISOString(),
          }
          const exists = prev?.some((m) => m.id === aiId)
          return exists
            ? prev.map((m) =>
                m.id === aiId
                  ? { ...errMsg, createdAt: m.createdAt || errMsg.createdAt }
                  : m
              )
            : [...(prev || []), errMsg]
        })
      } finally {
        setIsLoading(false)
        setCurrentStatus("")
      }
    },
    [
      sessionId,
      isLoading,
      streamPending,
      wsReady,
      wsSendEvent,
      sendMessage,
      pushUserBubble,
      setMessages,
      setIsLoading,
      setCurrentStatus,
      processEnvelopes,
      loadSessions,
    ]
  )

  const handleClearChat = useCallback(
    async (forceCreate?: boolean) => {
      // If the chat is already new/empty (has 0 messages), do nothing.
      const hasRealMessages = messagesRef.current.length > 0
      if (!hasRealMessages && forceCreate !== true) return

      sessionLoadGenRef.current += 1
      resetChatActivityState()
      setMessages([])
      setCurrentStatus(t("Starting new conversation...") as string)
      setInputMode("agent")
      setActiveTab("messages")

      const startFreshSession = (targetSessionId: string) => {
        // Mark before setSessionId so the [sessionId, messages.length] effect
        // doesn't trigger handleSessionSelect on this brand-new empty session.
        autoRestoredSessionsRef.current.add(targetSessionId)
        setSessionId(targetSessionId)
        beginEmptySessionGreeting(targetSessionId)
      }

      try {
        // Always create a NEW session instead of clearing the current one
        const response = await createSession("")
        const newSessionId: string = response?.data?.data?.session_id || ""
        if (!newSessionId) return
        startFreshSession(newSessionId)
        loadSessions()
      } catch {
        resetChatActivityState()
      }
    },
    [
      sessionId,
      resetChatActivityState,
      setMessages,
      setSessionId,
      setCurrentStatus,
      setInputMode,
      setActiveTab,
      setActiveView,
      loadSessions,
      beginEmptySessionGreeting,
    ]
  )

  useEffect(() => {
    if (!sessionIdRef.current) {
      void handleClearChat(true)
    }
  }, [])

  const openHelpArticle = useCallback((helpPath: string) => {
    setSelectedHelpCategory(null)
    setSelectedHelpPath(helpPath)
    setActiveTab("help")
  }, [])

  const handleRouteChipClick = useCallback(
    (chip: CopilotStarterChip) => {
      if (chip.helpPath || chip.id === "open_help") {
        if (chip.helpPath) openHelpArticle(chip.helpPath)
        return
      }
      setActiveTab("messages")
      setActiveView("chat")
      const mode = chip.id === "ask_question" ? "ask" : "agent"
      void sendMessage(chip.prompt?.trim() || chip.label, mode)
    },
    [sendMessage, openHelpArticle]
  )

  const renderPromptChipRow = (chip: CopilotStarterChip) => {
    const chipLabel = t(chip.label, { defaultValue: chip.label }) as string
    const isHelpLink = Boolean(chip.helpPath || chip.id === "open_help")
    const activate = () => {
      if (isHelpLink && chip.helpPath) {
        openHelpArticle(chip.helpPath)
        return
      }
      handleRouteChipClick(chip)
    }
    return (
      <Box
        key={`${chip.id}-${chip.label}-${chip.helpPath || ""}`}
        className={
          chip.primary ? "livis-ai-home-action-btn" : "livis-ai-home-link-btn"
        }
        role="button"
        tabIndex={0}
        aria-label={chipLabel}
        onClick={activate}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            activate()
          }
        }}
      >
        <Typography className="livis-ai-btn-title">{chipLabel}</Typography>
        <KeyboardArrowRightIcon
          sx={{ fontSize: 20, color: "var(--chat-text-2)" }}
          aria-hidden
        />
      </Box>
    )
  }

  const renderHomePromptGroupTitle = (title: string) => (
    <Typography component="p" className="livis-ai-home-prompt-group-title">
      {title}
    </Typography>
  )

  const renderRouteSuggestionBlock = () => {
    const actionChips = routePageContext.chips
    const helpChips = routePageContext.helpChips
    if (!actionChips.length && !helpChips.length) return null
    return (
      <>
        {actionChips.length > 0 ? (
          <Box className="livis-ai-home-prompt-group">
            {routePageContext.helpOnTitle
              ? renderHomePromptGroupTitle(routePageContext.helpOnTitle)
              : null}
            {actionChips.map((chip) => renderPromptChipRow(chip))}
          </Box>
        ) : null}
        {helpChips.length > 0 ? (
          <Box className="livis-ai-home-prompt-group">
            {renderHomePromptGroupTitle(t("Learn more") as string)}
            {helpChips.map((chip) => renderPromptChipRow(chip))}
          </Box>
        ) : null}
      </>
    )
  }

  const messagesWideLayout = mode === "fullscreen" || isExpanded || isWideMode

  useEffect(() => {
    if (isSidebarExpanded && setMode) {
      setMode("fullscreen")
    }
  }, [isSidebarExpanded, setMode])

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box
      className={`livis-ai-assistant-chat-window ${mode} ${
        isExpanded ? "expanded" : ""
      } ${deleteConfirmOpen ? "modal-open" : ""} ${
        isWideMode && !isExpanded ? "wide-mode" : ""
      } new-layout`}
    >
      {/* ── SIDEBAR ── */}
      <Box
        className={`livis-ai-sidebar ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <Box className="livis-ai-sidebar-header">
          <LivisCopilotIcon colorType="accent" width={24} height={24} />
          {isSidebarExpanded ? (
            <Box
              className="livis-ai-sidebar-title-group"
              sx={{ width: "100%" }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Typography className="livis-ai-sidebar-title">
                  {t("LIVIS Copilot")}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
                  className="livis-ai-sidebar-toggle"
                  style={{ transform: "rotate(180deg)" }}
                >
                  <CopilotSidebarIcon />
                </IconButton>
              </Box>
              <Typography className="livis-ai-sidebar-version">v1.0</Typography>
            </Box>
          ) : (
            <IconButton
              size="small"
              onClick={() => setIsSidebarExpanded(!isSidebarExpanded)}
              className="livis-ai-sidebar-toggle"
              style={{ transform: "none" }}
            >
              <CopilotSidebarIcon />
            </IconButton>
          )}
        </Box>

        <Box className="livis-ai-sidebar-menu">
          <Box
            className={`livis-ai-sidebar-item ${
              (activeTab === "home" || activeTab === "messages") &&
              !sessions.find((s) => s.session_id === sessionId)
                ? "active"
                : ""
            }`}
            onClick={() => {
              setActiveTab("messages")
              handleClearChat()
            }}
          >
            <CopilotEditIcon
              active={
                (activeTab === "home" || activeTab === "messages") &&
                !sessions.find((s) => s.session_id === sessionId)
              }
            />
            {isSidebarExpanded && <Typography>{t("New Chat")}</Typography>}
          </Box>
          <Box
            className={`livis-ai-sidebar-item ${
              activeTab === "alerts" ? "active" : ""
            }`}
            onClick={() => {
              setActiveTab("alerts")
            }}
          >
            {activeTab === "alerts" ? (
              <AlertBoldIcon
                style={{
                  width: 20,
                  height: 20,
                  color: "var(--livis-color-primary-blue)",
                }}
              />
            ) : (
              <CopilotAlertIcon />
            )}
            {isSidebarExpanded && <Typography>{t("Alerts")}</Typography>}
          </Box>
        </Box>

        <Box className="livis-ai-sidebar-history-section">
          <Box
            className={`livis-ai-sidebar-history-header ${
              activeView === "list" ? "active" : ""
            }`}
            onClick={() => {
              if (!isSidebarExpanded) setIsSidebarExpanded(true)
              else if (activeView === "list")
                setActiveView("chat") // toggle off if already open
              else setActiveView("list")
            }}
          >
            <CopilotHistoryIcon />
            {isSidebarExpanded && (
              <>
                <Typography>{t("Chat History")}</Typography>
                <KeyboardArrowDownIcon
                  fontSize="small"
                  className={`expand-icon ${
                    activeView === "list" ? "open" : ""
                  }`}
                />
              </>
            )}
          </Box>
          {isSidebarExpanded && activeView === "list" && (
            <Box className="livis-ai-sidebar-history-list">
              {sessions.map((s) => (
                <Box
                  key={s.session_id}
                  className={`livis-ai-sidebar-history-item ${
                    activeTab === "messages" && s.session_id === sessionId
                      ? "active"
                      : ""
                  }`}
                  onClick={() => {
                    handleSessionSelect(s.session_id)
                    setActiveTab("messages")
                  }}
                  sx={{
                    "& .livis-ai-sidebar-item-delete": {
                      opacity: 0,
                      transition: "opacity 0.15s",
                    },
                    "&:hover .livis-ai-sidebar-item-delete": { opacity: 1 },
                  }}
                >
                  <Typography noWrap className="title">
                    {s.summary || t("Conversation")}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <Typography noWrap className="time" sx={{ flex: 1 }}>
                      {formatSessionListTime(getSessionTimestamp(s))}
                    </Typography>
                    <Tooltip
                      title={t("Delete conversation") as string}
                      placement="top"
                      arrow
                    >
                      <IconButton
                        size="small"
                        className="livis-ai-sidebar-item-delete"
                        onClick={(e) => handleDeleteSession(e, s.session_id)}
                        sx={{
                          p: 0.25,
                          flexShrink: 0,
                          color: "var(--livis-gray-font-color)",
                          "&:hover": {
                            color: "var(--livis-color-red, #e53935)",
                          },
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: "0.85rem" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── MAIN CHAT AREA ── */}
      <Box className="livis-ai-main-area">
        {/* Header */}
        <Box className="livis-ai-main-header">
          <Box className="livis-ai-main-header-left">
            <Typography className="title">
              {activeTab === "alerts"
                ? t("Alerts")
                : sessions.find((s) => s.session_id === sessionId)?.summary ||
                  t("New chat")}
            </Typography>
            <Typography className="beta-badge">{t("BETA")}</Typography>
          </Box>
          <Box className="livis-ai-main-header-right">
            <IconButton
              onClick={() => {
                if (setMode) {
                  setMode(isExpanded ? "floating" : "fullscreen")
                }
              }}
            >
              {isExpanded ? (
                <CloseFullscreenIcon fontSize="small" />
              ) : (
                <OpenInFullIcon fontSize="small" />
              )}
            </IconButton>
            <IconButton onClick={onClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box className="livis-ai-main-content">
          {activeTab === "alerts" && (
            <Box className="livis-ai-alerts-view">
              <Box className="livis-ai-search-bar">
                <input
                  placeholder={t("Search any alerts to resolve") as string}
                  value={helpSearchQuery}
                  onChange={(e) => setHelpSearchQuery(e.target.value)}
                />
                <SearchIcon className="search-icon" />
              </Box>
              <Box className="livis-ai-alerts-list">
                {!Array.isArray(alerts) ||
                alerts.filter(
                  (a) =>
                    (a.title?.toLowerCase() || "").includes(
                      helpSearchQuery.toLowerCase()
                    ) ||
                    (a.message?.toLowerCase() || "").includes(
                      helpSearchQuery.toLowerCase()
                    )
                ).length === 0 ? (
                  <Typography className="livis-ai-alerts-empty">
                    {t("No recent alerts.")}
                  </Typography>
                ) : (
                  alerts
                    .filter(
                      (a) =>
                        (a.title?.toLowerCase() || "").includes(
                          helpSearchQuery.toLowerCase()
                        ) ||
                        (a.message?.toLowerCase() || "").includes(
                          helpSearchQuery.toLowerCase()
                        )
                    )
                    .map((alert: any, i: number) => (
                      <Box className="livis-ai-alert-card" key={alert?.id || i}>
                        <Box className="livis-ai-alert-header">
                          <Box className="livis-ai-alert-icon-wrapper">
                            <CopilotShapeIcon
                              style={{ color: "var(--livis-error-color)" }}
                            />
                          </Box>
                          <Box
                            className="livis-ai-alert-title-col"
                            sx={{ flex: 1 }}
                          >
                            <Typography className="livis-ai-alert-title">
                              {alert?.title ||
                                alert?.trigger_event ||
                                t("System Alert")}
                            </Typography>
                            <Typography className="livis-ai-alert-meta">
                              {alert?.notification_type || t("System Health")}{" "}
                              &nbsp;&nbsp;&nbsp;{" "}
                              {alert?.created_at
                                ? dayjs(alert.created_at).format(
                                    "MMM DD YYYY [at] h:mm A"
                                  )
                                : ""}
                            </Typography>

                            <Typography className="livis-ai-alert-body">
                              {(
                                alert?.message ||
                                t("New system notification received.")
                              )
                                .split(/(\*\*.*?\*\*|\*.*?\*)/g)
                                .map((part: string, index: number) => {
                                  if (
                                    part.startsWith("**") &&
                                    part.endsWith("**")
                                  ) {
                                    return (
                                      <strong key={index}>
                                        {part.slice(2, -2)}
                                      </strong>
                                    )
                                  } else if (
                                    part.startsWith("*") &&
                                    part.endsWith("*")
                                  ) {
                                    return (
                                      <strong key={index}>
                                        {part.slice(1, -1)}
                                      </strong>
                                    )
                                  }
                                  return <span key={index}>{part}</span>
                                })}
                            </Typography>
                            <Box className="livis-ai-alert-action-row">
                              <Button
                                className="livis-ai-alert-action-btn"
                                onClick={() => {
                                  const text = [alert?.title, alert?.message]
                                    .filter(Boolean)
                                    .join(" — ")
                                  setInputMode("ask")
                                  setActiveTab("messages")
                                  setActiveView("chat")
                                  sendMessage(text, "ask")
                                }}
                              >
                                {t("Ask LIVIS")}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    ))
                )}
              </Box>
            </Box>
          )}

          {activeTab === "messages" && (
            <>
              <Box
                className="livis-ai-assistant-messages"
                ref={messagesContainerCallbackRef}
              >
                <div
                  className={`livis-ai-assistant-messages-inner${
                    messagesWideLayout ? " is-wide" : ""
                  }${messages.length === 0 ? " is-empty" : ""}`}
                >
                  {hasMoreMessages && (
                    <Button
                      size="small"
                      onClick={loadMoreMessages}
                      sx={{
                        alignSelf: "center",
                        mb: 2,
                        textTransform: "none",
                        color: "var(--livis-gray-font-color)",
                      }}
                      disabled={isLoading}
                    >
                      {isLoading ? t("Loading...") : t("Load Earlier Messages")}
                    </Button>
                  )}

                  {messages.length === 0 && !isLoading && !streamPending && (
                    <Box className="livis-ai-empty-state">
                      <Box className="livis-ai-empty-state-icon">
                        <LivisCopilotIcon
                          colorType="accent"
                          width={40}
                          height={40}
                        />
                      </Box>
                      <Typography className="livis-ai-empty-state-headline">
                        <span className="bold-text">
                          {t("Welcome to LIVIS")}
                        </span>{" "}
                        <span
                          className="light-text"
                          style={{ color: "var(--livis-gray-font-color)" }}
                        >
                          {t("Copilot")}
                        </span>
                      </Typography>
                      <Typography className="livis-ai-empty-state-subtitle">
                        {t(
                          "Ask about the platform or let LIVIS Assist guide your setup needs."
                        )}
                      </Typography>
                      <Box className="livis-ai-suggestions">
                        <Box
                          className="livis-ai-suggestion-chip"
                          onClick={() =>
                            sendMessage("@ Create an AI Inspector", "agent")
                          }
                        >
                          <Typography>
                            {t("@ Create an AI Inspector")}
                          </Typography>
                        </Box>
                        <Box
                          className="livis-ai-suggestion-chip"
                          onClick={() =>
                            sendMessage(
                              "@ Set up my inspection project",
                              "agent"
                            )
                          }
                        >
                          <Typography>
                            {t("@ Set up my inspection project")}
                          </Typography>
                        </Box>
                        <Box
                          className="livis-ai-suggestion-chip"
                          onClick={() =>
                            sendMessage(
                              "✦ Monitor my production reports",
                              "agent"
                            )
                          }
                        >
                          <Typography>
                            {t("✦ Monitor my production reports")}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography className="livis-ai-empty-state-footer">
                        {t(
                          "@ to mention any station, AI Inspector or part and set context."
                        )}
                        <br />
                        {t("Tab to add current view to context")}
                      </Typography>
                    </Box>
                  )}

                  {messages?.map((msg) => (
                    <div
                      key={msg?.id}
                      data-message-id={msg.id}
                      className={`livis-ai-message-anchor ${
                        msg.type === "ai" && (msg.text || "").length > 600
                          ? "livis-ai-big-summary"
                          : ""
                      }`}
                    >
                      <MessageBubble
                        msg={msg}
                        sessionId={sessionId}
                        inputLocked={inputLocked}
                        onMarkerAction={handleMarkerAction}
                        onUploadComplete={handleUploadComplete}
                        loadingStatus={
                          msg.isLoading ? currentStatus : undefined
                        }
                        onFeedback={handleMessageFeedback}
                        onRegenerate={handleRegenerateMessage}
                        showRegenerate={msg?.id === lastRegenerableAiMsgId}
                        canEditUser={
                          msg?.type === "user" &&
                          msg?.id === lastEditableUserMsgId &&
                          !inputLocked
                        }
                        isEditingUser={msg?.id === editingUserMsgId}
                        editDraft={
                          msg?.id === editingUserMsgId
                            ? editUserDraft
                            : undefined
                        }
                        onStartEditUser={() => {
                          setEditingUserMsgId(msg.id)
                          setEditUserDraft(msg.text || "")
                        }}
                        onEditDraftChange={setEditUserDraft}
                        onCancelEditUser={() => {
                          setEditingUserMsgId(null)
                          setEditUserDraft("")
                        }}
                        onSaveEditUser={() =>
                          handleEditUserMessage(msg.id, editUserDraft)
                        }
                      />
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </Box>

              <Box className="livis-ai-main-footer">
                <ChatInputArea
                  isLoading={inputLocked}
                  onSend={(text, mentions) => {
                    if (editingUserMsgId) {
                      handleEditUserMessage(editingUserMsgId, text)
                      return
                    }
                    void sendMessage(text, inputMode, { mentions })
                  }}
                  onCancel={cancelStream}
                  inputMode={inputMode}
                  onModeChange={setInputMode}
                  onMentionSearch={searchMentions}
                  onMentionsChange={setPendingMentions}
                  activeContext={effectiveContext}
                  isDisconnected={isWsConnecting}
                  onClearContext={() => {
                    wsUnbindWork()
                    setWorkBinding(null)
                    setPendingMentions([])
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Delete Confirmation Modal */}
      <LivisModal
        size="small"
        animation_type="slideup"
        modal_open={deleteConfirmOpen}
        title={t("Confirm Delete")}
        livis_dismiss_btn={() => { document.body.classList.remove("livis-ai-modal-open"); setDeleteConfirmOpen(false) }}
        disable_outside_click={true}
        cancel_icon={true}
        livis_modal_class="livis-confirm-close-modal livis-ai-assistant-caution-modal"
      >
        <Box sx={{ p: 1 }}>
          <p
            style={{
              marginBottom: "1.5rem",
              fontSize: "0.95rem",
              color: "var(--livis-black-font-color)",
            }}
          >
            {t(
              "Are you sure you want to delete this conversation? This action cannot be undone."
            )}
          </p>
          <Grid container className="livis-upload-error-modal-container">
            <Button
              variant="outlined"
              size="medium"
              className="livis-modal-btn-round livis-modal-cancel-close-btn"
              onClick={() => { document.body.classList.remove("livis-ai-modal-open"); setDeleteConfirmOpen(false) }}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              size="medium"
              className="livis-modal-btn-round livis-modal-cancel-danger-btn"
              onClick={confirmDelete}
            >
              {t("Yes, Delete")}
            </Button>
          </Grid>
        </Box>
      </LivisModal>
    </Box>
  )
}

export default AIChatWindow
